/// <reference path="../apple-pay.d.ts" />
'use client';

import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';
import Script from 'next/script';

import { useTranslation } from 'react-i18next';
import { ClassCancellationDisclosure } from './class-cancellation-disclosure';

import {
  type AppliedDiscount,
  DiscountCodeInput,
} from '@kit/booking-addons/components/discount-code-input';
import {
  type MembershipPerkPreview,
  previewMembershipPerkAction,
} from '@kit/booking-addons/server/actions/discount-code-actions';
import { BookingErrorCode } from '@kit/bookings/constants';
import { Capabilities } from '@kit/capabilities';
import { useCapabilityOptional } from '@kit/capabilities/client';
import { buildPaymentDescription } from '@kit/payments/description';
import { formatTimeToAmPm } from '@kit/shared/utils';
import { Button } from '@kit/ui/button';
import { Calendar } from '@kit/ui/calendar';
import { CardContent } from '@kit/ui/card';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { RadioGroup } from '@kit/ui/radio-group';
import { Skeleton } from '@kit/ui/skeleton';
import { toast } from '@kit/ui/sonner';
import { cn } from '@kit/ui/utils';

import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';
import {
  activeLocationCookieKey,
  dispatchActiveLocationChanged,
} from '~/lib/tenant/active-location-cookie';

import { getBookingDateRange } from '../_lib/booking-date-range';
import {
  pickPolicyText,
  policyTextToBullets,
} from '../_lib/booking-policy-config';
import { selectCourtLabel } from '../_lib/format-court-label';
import { useBookingEffects } from '../_lib/hooks/use-booking-effects';
import {
  isClassType,
  isGenericClassType,
  useBookingReducer,
} from '../_lib/hooks/use-booking-reducer';
import type { SelectedPackage } from '../_lib/hooks/use-booking-reducer';
import { useMarketingUser } from '../_lib/hooks/use-marketing-user';
import {
  type HyperpayCheckoutData,
  type MoyasarCheckoutData,
  usePackagePurchase,
} from '../_lib/hooks/use-package-purchase';
import { shouldShowServiceList } from '../_lib/is-service-list-sport';
import {
  type ApplePaySessionParams,
  initiateApplePaySession,
} from '../_lib/payment/apple-pay';
import {
  type CheckoutCallbacks,
  type CheckoutContext,
  processCheckout,
} from '../_lib/payment/checkout';
import {
  getWalletPaymentAvailability,
  shouldHideClassWalletPayment,
} from '../_lib/payment/wallet-availability';
import { publicCodesForSlotSurface } from '../_lib/public-discount-codes';
import { scrollToBookingElement } from '../_lib/booking-scroll-container';
import { scrollToVisibleBookingSection } from '../_lib/scroll-to-booking-section';
import type { BookingPageData, BookingDataPatch } from '../_lib/server/booking-page.loader';
import { BookingDataStream } from './booking-data-stream';
import {
  checkWaitlistStatusAction,
  leaveClassWaitlistAction,
} from '../_lib/server/class-waitlist-actions';
import { checkUserPackageCreditsAction } from '../_lib/server/court-booking/court-booking-with-package';
import {
  getAllBookingSlotsBatch,
  getAvailableBookingTypes,
  getAvailableClassesForDate,
  validateLocationAccessCode,
} from '../_lib/server/server-actions';
import { getBookingWalletBalanceAction } from '../_lib/server/wallet-balance-action';
import {
  filterTimeSlotsByStaff,
  getAvailableStaff,
} from '../_lib/staff-availability';
import CourtItem from './CourtItem';
import { AccountSelector } from './account-selector';
import { BookingPolicyNotice } from './booking-policy-notice';
import { BallMachineSelection } from './ball-machine-selection';
import {
  BookingPackageSuggestions,
  BookingPageSurface,
  BookingSection as Card,
} from './booking-presentation';
import {
  ALL_CLASSES_BOOKING_TYPE,
  BookingTypeToggle,
  resolveBookingTypeChip,
} from './booking-type-toggle';
import { CheckoutSuccessDialog } from './checkout-success-dialog';
import { ClassSelection } from './class-selection';
import { CustomerAddonSelector } from './customer-addon-selector';
import { DateStrip } from './date-strip';
import { GuestRegistrationDrawer } from './guest-registration-drawer';
import { LocationFunnelPicker } from './location-funnel-picker';
import { LocationSelector } from './location-selector';
import { PackageSelection } from './package-selection';
import { ParticipantSelector } from './participant-selector';
import {
  type PaymentMethod,
  PaymentMethodSelector,
} from './payment-method-selector';
import { ProfileCompletionDialog } from './profile-completion-dialog';
import { ServiceList } from './service-list';
import { SingleCourtHero } from './single-court-hero';
import { SportFilterToggle } from './sport-filter-toggle';
import { StaffAvailabilityFilter } from './staff-availability-filter';
import { TrainerSelection } from './trainer-selection';

function CheckoutLoading() {
  return <div role="status" className="fixed inset-0 z-50 flex items-center justify-center bg-background/80">
    <Spinner />
    <span className="sr-only"><Trans i18nKey="common:loading" /></span>
  </div>;
}

const HyperpayCheckoutDialog = dynamic(() => import('./hyperpay-checkout-dialog').then((module) => module.HyperpayCheckoutDialog), { loading: CheckoutLoading });
const MoyasarCheckoutDialog = dynamic(() => import('./moyasar-checkout-dialog').then((module) => module.MoyasarCheckoutDialog), { loading: CheckoutLoading });
const StripeCheckoutForm = dynamic(() => import('./stripe-checkout-form').then((module) => module.StripeCheckoutForm), { loading: CheckoutLoading });
const PackageCheckoutDialog = dynamic(() => import('./package-checkout-dialog').then((module) => module.PackageCheckoutDialog), { loading: CheckoutLoading });

// Map a booking-service error code to a localized, user-facing toast string.
// Services return stable codes (never localized copy); the client localizes.
// Shared by every checkout onError handler (standard + Apple Pay) so a new code
// only needs to be mapped once.
function localizeBookingError(
  message: string,
  t: (key: string) => string,
): string {
  if (message === BookingErrorCode.OutsideOperatingHours) {
    return t('outsideOperatingHours');
  }
  return message;
}

function formatDateForDiscount(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isPostMidnight(time24: string): boolean {
  const [hours] = time24.split(':').map(Number);
  return (hours || 0) >= 0 && (hours || 0) < 5;
}

function calculateEndTime(startTime: string, durationMinutes: number): string {
  const [hours, minutes] = startTime.split(':').map(Number);
  const startDate = new Date();
  startDate.setHours(hours || 0, minutes || 0, 0, 0);
  const endDate = new Date(startDate.getTime() + durationMinutes * 60000);
  return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
}

interface BookingPresets {
  bookingType?: string;
  sport?: string;
  duration?: number;
  date?: string;
  /** HH:MM or HH:MM:SS start time to auto-select once slots load */
  time?: string;
  /** Coach name to auto-select after time is chosen */
  coachName?: string;
  /** Class template ID to auto-select once classes load */
  classTemplateId?: string;
  /** Booking package ID to auto-open the package checkout dialog for. */
  packageId?: string;
}

interface BookingFormProps {
  initialData: BookingPageData;
  deferredData: Promise<BookingDataPatch>[];
  presets?: BookingPresets;
}

export function BookingForm({ initialData: bootstrapData, deferredData, presets }: BookingFormProps) {
  const [enrichment, setEnrichment] = useState<BookingDataPatch>({});
  const receiveData = useCallback((data: BookingDataPatch) => {
    setEnrichment((previous) => ({ ...previous, ...data }));
  }, []);
  const initialData = useMemo(() => ({
    ...bootstrapData,
    ...enrichment.checkout,
    ...enrichment.offers,
    ...(enrichment.packages ? {
      packages: enrichment.packages.packages,
      firstTimePackageId: enrichment.packages.firstTimePackageId,
    } : {}),
    profileCompletion: enrichment.profileCompletion ?? bootstrapData.profileCompletion,
  }), [bootstrapData, enrichment]);
  const checkoutReady = Boolean(enrichment.checkout && enrichment.packages && enrichment.offers);

  const isNativeLayout = useNativeLayout();
  const router = useRouter();
  const { t, i18n } = useTranslation('bookings');
  const isRTL = i18n.language === 'ar';
  // Arabic month/weekday labels for the desktop month-grid calendar. Mirrors
  // DateStrip's Intl('ar') approach (no date-fns locale dependency). Plain 'ar'
  // (not 'ar-SA') keeps the Gregorian calendar — 'ar-SA' defaults to Hijri,
  // which is confusing for a Gregorian booking calendar. Only
  // applied in Arabic; English uses react-day-picker's defaults. Passed to the
  // <Calendar> formatters prop, which the shadcn wrapper merges with its own.
  const calendarFormatters = useMemo(() => {
    if (!isRTL) return undefined;
    const monthCaption = new Intl.DateTimeFormat('ar', {
      month: 'long',
      year: 'numeric',
    });
    const weekday = new Intl.DateTimeFormat('ar', { weekday: 'short' });
    return {
      formatCaption: (date: Date) => monthCaption.format(date),
      formatWeekdayName: (date: Date) => weekday.format(date),
    };
  }, [isRTL]);
  const unifiedClassMode = useCapabilityOptional(
    Capabilities.UNIFIED_CLASS_FILTER,
  );
  const applePayButtonRef = useRef<HTMLElement | null>(null);
  const nextSectionRef = useRef<HTMLDivElement>(null);
  const extrasSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  const [state, dispatch] = useBookingReducer(initialData.initialLocationUuid);
  const availableStaff = useMemo(
    () =>
      state.bookingType === 'trainer' ? getAvailableStaff(state.timeSlots) : [],
    [state.bookingType, state.timeSlots],
  );
  const visibleTimeSlots = useMemo(
    () =>
      state.bookingType === 'trainer'
        ? filterTimeSlotsByStaff(state.timeSlots, state.trainerFilterId)
        : state.timeSlots,
    [state.bookingType, state.timeSlots, state.trainerFilterId],
  );
  const [calendarMonth, setCalendarMonth] = useState(state.date);
  // Whether the customer has ticked the acceptance checkbox. Only meaningful
  // when the tenant enabled `bookingPolicy.requireAcceptance`; ignored otherwise.
  const [hasAcceptedPolicy, setHasAcceptedPolicy] = useState(false);

  // Keep the calendar's displayed month in sync when state.date changes
  // (e.g. auto-selecting the first available class date)
  useEffect(() => {
    setCalendarMonth(state.date);
  }, [state.date]);

  const { data: user, isLoading: isUserLoading } = useMarketingUser();

  // Load the customer's wallet balance + tenant wallet-flag once authenticated,
  // to drive the "Pay with Wallet" option (full-cover only). Mirrors the
  // package-credits fetch; wallet balance doesn't change with slot selection,
  // so this only depends on the user + tenant.
  useEffect(() => {
    if (!user?.id || !initialData.accountId) {
      dispatch({
        type: 'SET_WALLET',
        payload: { walletBalance: 0, walletEnabled: false },
      });
      return;
    }

    let cancelled = false;

    getBookingWalletBalanceAction({ accountId: initialData.accountId })
      .then((result) => {
        if (cancelled) return;
        dispatch({
          type: 'SET_WALLET',
          payload: {
            walletBalance: result.walletBalance,
            walletEnabled: result.walletEnabled,
          },
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error loading wallet balance:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, initialData.accountId, dispatch]);

  const handleTimeSelect = useCallback(
    (time: string) => {
      dispatch({ type: 'SELECT_TIME', payload: time });
      // Scroll to the next section (court/resource selection) after a brief delay
      // to let the UI update with the new selection
      setTimeout(() => {
        const el = nextSectionRef.current;
        if (!el) return;
        scrollToBookingElement(el, 80);
      }, 100);
    },
    [dispatch],
  );

  const handleCourtSelect = useCallback(
    (courtId: string) => {
      dispatch({ type: 'SELECT_COURT', payload: courtId });
      setTimeout(() => {
        const el = paymentSectionRef.current;
        if (!el) return;
        scrollToBookingElement(el, 80);
      }, 100);
    },
    [dispatch],
  );

  const desktopServiceTimeSectionRef = useRef<HTMLDivElement>(null);
  const mobileServiceTimeSectionRef = useRef<HTMLDivElement>(null);

  const handleServiceSelect = useCallback(
    (templateId: string, durationLabel: string) => {
      dispatch({
        type: 'SELECT_SERVICE',
        payload: { templateId, durationLabel },
      });
      // Let the Time card render (it appears once a duration is set), then
      // scroll it into view so the customer continues downward naturally.
      setTimeout(() => {
        // Resolve visibility now so resizing never leaves a stale layout ref.
        scrollToVisibleBookingSection([
          desktopServiceTimeSectionRef.current,
          mobileServiceTimeSectionRef.current,
        ]);
      }, 150);
    },
    [dispatch],
  );

  // Package dialog local state (not part of booking flow)
  const [isPackageDialogOpen, setIsPackageDialogOpen] = React.useState(false);

  // Profile-completion popup (booking page only). Mirrors the first-time offer
  // popup: a one-shot, session-dismissible nudge — but sequenced *after* the
  // first-time offer so the two dialogs never stack. Driven by the lightweight
  // missing-required-fields check folded into the booking-page loader.
  const autoOpenedProfileRef = React.useRef(false);
  const [isProfilePromptOpen, setIsProfilePromptOpen] = React.useState(false);
  // True once the first-time offer step is over (never eligible, or shown and
  // closed). Until then the profile popup waits its turn. Computed up front so
  // the profile effect doesn't race the first-time auto-open on initial mount.
  const [firstTimeOfferResolved, setFirstTimeOfferResolved] = React.useState(
    Boolean(presets?.packageId),
  );

  const initialPackagesMatchLocation = Boolean(enrichment.packages && (
    enrichment.packages.locationId === state.selectedLocationId ||
    (state.selectedLocationId === null && state.locations.length === 1 &&
      state.locations[0]?.id === enrichment.packages.locationId)
  ));
  useEffect(() => {
    if (!enrichment.packages) return;
    const offer = enrichment.packages.packages.find(
      (pkg) => pkg.id === enrichment.packages?.firstTimePackageId,
    );
    const dismissed = offer && window.sessionStorage.getItem(
      `firstTimeOffer:dismissed:${bootstrapData.tenantId}:${offer.id}`,
    );
    if (!initialPackagesMatchLocation || !offer || offer.is_fixed_slot || dismissed) {
      setFirstTimeOfferResolved(true);
    }
  }, [enrichment.packages, initialPackagesMatchLocation, bootstrapData.tenantId]);

  // Deep-link from /packages catalog: ?package=<id> auto-opens the checkout
  // dialog for that package. One-shot — guarded by a ref so it doesn't re-fire
  // if the user closes the dialog and the effect deps change for other reasons.
  const autoOpenedPackageRef = React.useRef(false);
  React.useEffect(() => {
    if (autoOpenedPackageRef.current || !checkoutReady || !initialPackagesMatchLocation) return;
    const targetId = presets?.packageId;
    if (!targetId) return;
    const match = initialData.packages.find((p) => p.id === targetId);
    if (!match) return;
    autoOpenedPackageRef.current = true;
    if (match.is_fixed_slot) {
      router.push(`/book/packages?package=${match.id}`);
      return;
    }
    dispatch({
      type: 'SET_SELECTED_PACKAGE',
      payload: {
        id: match.id,
        name: match.name,
        price: match.price,
        booking_count: match.booking_count,
        slot_duration: match.slot_duration,
        validity_months: match.validity_months,
        validity_days: match.validity_days,
        original_price: match.original_price,
        package_type: match.package_type,
        player_pricing: match.player_pricing,
        description: match.description,
        features: match.features,
        color: match.color,
        max_subscribers: match.max_subscribers,
        location_id: match.location_id,
        is_bundle: match.is_bundle,
        require_attendance_within_validity:
          match.require_attendance_within_validity,
        fixed_starts_at: match.fixed_starts_at,
        fixed_ends_at: match.fixed_ends_at,
      },
    });
    setIsPackageDialogOpen(true);
  }, [checkoutReady, initialPackagesMatchLocation, presets?.packageId, initialData.packages, dispatch, router]);

  // First-time offer popup: if the visitor is eligible for a first-time-only
  // package, auto-open the checkout dialog as if they had clicked it. Skipped
  // when a deep-link package is already targeted, and dismiss persists for the
  // current browser session via sessionStorage.
  const autoOpenedFirstTimeRef = React.useRef(false);
  React.useEffect(() => {
    if (autoOpenedFirstTimeRef.current || !checkoutReady || !initialPackagesMatchLocation) return;
    if (presets?.packageId) return;
    const targetId = initialData.firstTimePackageId;
    if (!targetId) return;
    const match = initialData.packages.find((p) => p.id === targetId);
    if (!match) return;
    // Don't auto-open the regular dialog for fixed-slot first-time offers;
    // those need the /book/packages grid. Visitor can still pick the card.
    if (match.is_fixed_slot) return;

    const dismissKey = `firstTimeOffer:dismissed:${initialData.tenantId}:${targetId}`;
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(dismissKey)
    ) {
      return;
    }

    autoOpenedFirstTimeRef.current = true;
    dispatch({
      type: 'SET_SELECTED_PACKAGE',
      payload: {
        id: match.id,
        name: match.name,
        price: match.price,
        booking_count: match.booking_count,
        slot_duration: match.slot_duration,
        validity_months: match.validity_months,
        validity_days: match.validity_days,
        original_price: match.original_price,
        package_type: match.package_type,
        player_pricing: match.player_pricing,
        description: match.description,
        features: match.features,
        color: match.color,
        max_subscribers: match.max_subscribers,
        location_id: match.location_id,
        is_bundle: match.is_bundle,
        require_attendance_within_validity:
          match.require_attendance_within_validity,
        fixed_starts_at: match.fixed_starts_at,
        fixed_ends_at: match.fixed_ends_at,
      },
    });
    setIsPackageDialogOpen(true);
  }, [
    presets?.packageId,
    checkoutReady,
    initialPackagesMatchLocation,
    initialData.firstTimePackageId,
    initialData.packages,
    initialData.tenantId,
    dispatch,
  ]);

  // Profile-completion popup: once the first-time offer step is resolved, nudge
  // a signed-in customer who still has required profile fields blank. Opens on
  // every booking-page visit (a fresh mount re-arms the ref); the ref only
  // stops it re-opening within the same visit after dismissal. Dismissible —
  // never blocks the booking.
  React.useEffect(() => {
    if (autoOpenedProfileRef.current) return;
    if (initialData.profileCompletion.missingFieldLabels.length === 0) return;
    if (!firstTimeOfferResolved) return;
    if (isPackageDialogOpen) return;
    autoOpenedProfileRef.current = true;
    setIsProfilePromptOpen(true);
  }, [
    initialData.profileCompletion.missingFieldLabels.length,
    firstTimeOfferResolved,
    isPackageDialogOpen,
  ]);

  // Discount code (preview-applied on book page; redeemed on payment page after redirect)
  const [appliedDiscount, setAppliedDiscount] =
    useState<AppliedDiscount | null>(null);

  // Membership perk preview (auto-applied member discount). Fetched once per
  // (account, template, bookingType) — perk metadata is stable for the flow.
  // The actual SAR amount per slot is computed locally based on bookingPrice.
  const [perkPreview, setPerkPreview] = useState<MembershipPerkPreview | null>(
    null,
  );

  useEffect(() => {
    if (
      !state.isMember ||
      !state.tenantAccountId ||
      !state.selectedTemplateId ||
      !state.bookingType ||
      isClassType(state.bookingType)
    ) {
      setPerkPreview(null);
      return;
    }

    let cancelled = false;
    void previewMembershipPerkAction({
      accountId: state.tenantAccountId,
      templateId: state.selectedTemplateId,
      bookingType: state.bookingType,
    })
      .then((res) => {
        if (!cancelled) setPerkPreview(res);
      })
      .catch(() => {
        if (!cancelled) setPerkPreview(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    state.isMember,
    state.tenantAccountId,
    state.selectedTemplateId,
    state.bookingType,
  ]);

  // The selected slot's server-resolved member-perk delta (original − final).
  // Single source of truth — the RPC has already vetted membership, day-of-
  // week, hour window, and template/package scope, so we trust whatever
  // `original_price` / `final_price` it returned for the user's selected
  // court/resource. The strikethrough, the "Member discount applied" pill,
  // and the discount-code banner-hide all read from this.
  //
  // Codes (when applied) take precedence over perks (no stacking).
  const perkAmountForCurrentSlot = useMemo(() => {
    if (appliedDiscount) return null; // code wins
    if (!state.selectedTime) return null;

    const selectedSlot = state.timeSlots.find(
      (s) => s.start_time === state.selectedTime,
    );
    if (!selectedSlot) return null;

    let original: number | null | undefined;
    let final: number | null | undefined;
    if (state.bookingType === 'court') {
      const court = selectedSlot.available_courts?.find(
        (c) => c.id === state.selectedCourt,
      );
      original = court?.original_price ?? selectedSlot.original_price;
      final = court?.final_price ?? selectedSlot.final_price;
    } else {
      const selectedResourceId =
        state.bookingType === 'trainer'
          ? state.selectedTrainer
          : state.selectedBallMachine;
      const resource = selectedSlot.available_resources?.find(
        (r) => r.id === selectedResourceId,
      );
      original = resource?.original_price;
      final = resource?.final_price;
    }

    if (original == null || final == null || original <= final) return null;
    const amount = Number((original - final).toFixed(2));
    if (amount <= 0) return null;

    // Decorate the pill with the perk name when perk-preview has loaded;
    // fall back to a generic label otherwise. Pill visibility is gated on
    // `amount > 0`, never on whether the preview fetch resolved.
    const labelled = perkPreview?.applies ? perkPreview : null;
    return {
      amount,
      name: labelled?.name,
      planName: labelled?.planName,
    };
  }, [
    appliedDiscount,
    perkPreview,
    state.selectedTime,
    state.timeSlots,
    state.bookingType,
    state.selectedCourt,
    state.selectedTrainer,
    state.selectedBallMachine,
  ]);

  // Total before any discount-code is applied. The membership perk is already
  // baked into `bookingPrice` (slot.final_price), so don't subtract it again.
  const subtotalBeforeCode =
    (state.bookingPrice ?? 0) + (state.addonsTotalPrice ?? 0);

  // Effective total to display & charge: subtotal minus the previewed code,
  // floored at 0. Falls back to subtotal when no code is applied.
  const effectiveTotal = Math.max(
    0,
    subtotalBeforeCode - (appliedDiscount?.amountOff ?? 0),
  );

  // Booking is free when the slot is fully picked and the resulting total is 0
  // (e.g. 100% discount coupon, complimentary booking type). Skip the gate
  // when bookingPrice is still null — no slot picked yet.
  const isFreeBooking = state.bookingPrice !== null && effectiveTotal === 0;

  // Member-comp preview for a SPLIT: when the member's auto-applied perk equals
  // exactly one undiscounted 1/N share, their own share is covered and the
  // booking confirms once the others pay. This MIRRORS the server's
  // resolveMemberCompShare so the displayed per-share + CTA match what
  // initializeSplitPaymentAction will actually create. No discount code (codes
  // never comp), a member perk present, and perkAmount == undiscounted/N.
  const splitComp = useMemo(() => {
    const perk = perkAmountForCurrentSlot?.amount ?? 0;
    if (appliedDiscount || perk <= 0 || state.splitPayerCount < 2) return null;
    // The server comps against the slot's UNDISCOUNTED base price (total_amount),
    // NOT including addons — the perk is discount on the slot only. Reconstruct
    // that base = discounted slot price (bookingPrice) + the perk given back.
    // Addons are billed separately and don't change the comp share, so they're
    // intentionally excluded here to match resolveMemberCompShare exactly.
    const undiscountedSlotBase = (state.bookingPrice ?? 0) + perk;
    const share =
      Math.round((undiscountedSlotBase / state.splitPayerCount) * 100) / 100;
    if (Math.abs(perk - share) > 0.01) return null;
    // Cash the OTHER N-1 players each pay; the member pays 0.
    return { share, othersPay: share };
  }, [
    appliedDiscount,
    perkAmountForCurrentSlot?.amount,
    state.bookingPrice,
    state.splitPayerCount,
  ]);

  const isSplitComp = state.paymentMethod === 'split' && splitComp !== null;

  // Pre-perk subtotal — the price the customer would pay if they weren't a
  // member, used to render a strike-through when a member perk is auto-applied.
  // Sourced from the SELECTED court's `original_price` (accurate when a slot
  // has multiple courts at different price tiers) or the selected resource's
  // `original_price` (trainer/ball_machine), with the slot-level value as a
  // single-tier fallback. Only meaningful when the slot's `member_discount`
  // metadata is set — otherwise the prices come from the same template tier
  // and a strike would be misleading (e.g. multi-template court selection).
  const subtotalBeforePerk = useMemo(() => {
    const selectedSlot = state.timeSlots.find(
      (s) => s.start_time === state.selectedTime,
    );
    if (!selectedSlot?.member_discount) return null;
    let original: number | null = null;
    if (state.bookingType === 'court') {
      const selectedCourtInfo = selectedSlot.available_courts?.find(
        (c) => c.id === state.selectedCourt,
      );
      original =
        selectedCourtInfo?.original_price ??
        selectedSlot.original_price ??
        null;
    } else if (selectedSlot.available_resources) {
      const selectedResourceId =
        state.bookingType === 'trainer'
          ? state.selectedTrainer
          : state.selectedBallMachine;
      const resource = selectedSlot.available_resources.find(
        (r) => r.id === selectedResourceId,
      );
      original = resource?.original_price ?? null;
    }
    if (original == null) return null;
    return original + (state.addonsTotalPrice ?? 0);
  }, [
    state.timeSlots,
    state.selectedTime,
    state.bookingType,
    state.selectedCourt,
    state.selectedTrainer,
    state.selectedBallMachine,
    state.addonsTotalPrice,
  ]);

  // Strike-through reference price. Prefer the (largest) pre-discount amount
  // we can find: a typed code stacks on top of the perk so we still want to
  // show the perk's pre-discount price; otherwise just the perk's pre-discount
  // price; otherwise no strike.
  const strikeReference =
    subtotalBeforePerk != null && subtotalBeforePerk > effectiveTotal
      ? subtotalBeforePerk
      : appliedDiscount && subtotalBeforeCode > effectiveTotal
        ? subtotalBeforeCode
        : null;

  // The previewed discount is computed against a specific slot + total. If the
  // user changes any of those, the preview no longer matches what would
  // actually be redeemed, so drop it and force re-application.
  useEffect(() => {
    if (appliedDiscount) setAppliedDiscount(null);
    // We intentionally don't depend on appliedDiscount itself — that would
    // create a one-shot loop on first apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.date?.getTime(),
    state.selectedTime,
    state.selectedDuration,
    state.selectedCourt,
    state.selectedClass,
    state.bookingPrice,
    state.addonsTotalPrice,
  ]);

  // Free bookings are complimentary, not cash transactions. This internal
  // method is not rendered as an option; it only routes a zero-total booking
  // through the gateway-free server path.
  useEffect(() => {
    if (isFreeBooking && state.paymentMethod !== 'complimentary') {
      dispatch({ type: 'SET_PAYMENT_METHOD', payload: 'complimentary' });
    } else if (!isFreeBooking && state.paymentMethod === 'complimentary') {
      dispatch({
        type: 'SET_PAYMENT_METHOD',
        payload: state.isApplePayAvailable ? 'applepay' : 'card',
      });
    }
  }, [isFreeBooking, state.isApplePayAvailable, state.paymentMethod, dispatch]);

  // Initialize from server data
  useEffect(() => {
    const data = bootstrapData.bookingTypesResult.data;
    if (!data) return;

    dispatch({
      type: 'INIT_DATA',
      payload: {
        bookingTypes: data.bookingTypes,
        sports: data.sports,
        durations: data.durations,
        durationsByBookingType: data.durationsByBookingType,
        templates: data.templates,
        locations: data.locations,
        packages: bootstrapData.packages,
        isMember: bootstrapData.membership.isMember,
        membershipId: bootstrapData.membership.membershipId,
        publishableKey: bootstrapData.publishableKey,
        provider: bootstrapData.provider,
        accountId: bootstrapData.accountId,
        enableCashPayment: data.enableCashPayment ?? true,
        classPackageCard: data.classPackageCard ?? true,
        enableSplitPayment: data.enableSplitPayment ?? true,
        maxAdvanceBookingDays: data.maxAdvanceBookingDays ?? null,
      },
    });

    if (bootstrapData.externalAddons.length > 0) {
      dispatch({
        type: 'SET_AVAILABLE_ADDONS',
        payload: bootstrapData.externalAddons,
      });
    }

    if (bootstrapData.initialLocationId && data.locations?.length) {
      // initialLocationId is a slug from the URL — resolve to UUID
      const matchedLocation = data.locations.find(
        (l) =>
          l.slug === bootstrapData.initialLocationId ||
          l.id === bootstrapData.initialLocationId,
      );
      if (matchedLocation) {
        dispatch({
          type: 'SET_LOCATION_DATA',
          payload: {
            locationId: matchedLocation.id,
            bookingTypes: data.bookingTypes,
            sports: data.sports,
            durations: data.durations,
            durationsByBookingType: data.durationsByBookingType,
            templates: data.templates,
            packages: bootstrapData.packages,
          },
        });
      }
    }

    // Studios default to the unified "All Classes" view. Presets below still
    // win — a deep link with ?type=... or a package perk overrides this.
    if (unifiedClassMode) {
      dispatch({
        type: 'SELECT_BOOKING_TYPE',
        payload: ALL_CLASSES_BOOKING_TYPE,
      });
    }

    // Apply URL presets (e.g. from coaches page: ?type=trainer&activity=padel&duration=60&date=2026-03-19)
    if (presets?.sport) {
      dispatch({ type: 'SELECT_SPORT', payload: presets.sport });
    }

    if (presets?.bookingType) {
      // Guard: 'all' is the synthetic studio-only filter. If a non-studio
      // tenant somehow gets ?type=all (shared link, stale bookmark), ignore
      // it so the pill row doesn't end up with no active selection.
      const isAllOnNonStudio =
        presets.bookingType === ALL_CLASSES_BOOKING_TYPE && !unifiedClassMode;
      if (!isAllOnNonStudio) {
        dispatch({
          type: 'SELECT_BOOKING_TYPE',
          payload: presets.bookingType,
        });
      }
    }

    if (presets?.duration) {
      // A bare ?duration= preset can't disambiguate when the sport + booking
      // type has 2+ templates at the same duration — that case shows the
      // service list, where the customer picks the exact named service. Skip
      // the preset there so the service list stays the entry point.
      const presetSport = presets.sport;
      const presetSportTemplates = presetSport
        ? (bootstrapData.bookingTypesResult.data?.templates?.[presetSport] ?? [])
        : [];
      const presetTypeTemplates = presetSportTemplates.filter((tmpl) => {
        if (presets.bookingType === 'court') return tmpl.has_court_booking;
        if (presets.bookingType === 'trainer') return tmpl.has_trainer_booking;
        if (presets.bookingType === 'ball_machine')
          return tmpl.has_ball_machine_booking;
        return true;
      });

      // No preset booking type ⇒ no court-specific context, so fall back to the
      // plain duration-collision rule (empty string is never 'court').
      if (
        !shouldShowServiceList(
          presets.bookingType ?? '',
          presetTypeTemplates,
          bootstrapData.businessType,
        )
      ) {
        // Duration labels are i18n-formatted (e.g. "60 Min" in en).
        // Use the same translation key the duration selector uses so
        // getDurationFromLabel can match it.
        dispatch({
          type: 'SELECT_DURATION',
          payload: t('minDuration', { count: presets.duration }),
        });
      }
    }

    if (presets?.date) {
      const [y, m, d] = presets.date.split('-').map(Number);
      if (y && m && d) {
        dispatch({
          type: 'SELECT_DATE',
          payload: new Date(y, m - 1, d),
        });
      }
    }
  }, [bootstrapData, dispatch, presets, unifiedClassMode]);

  // Enrichment patches never replay INIT_DATA or the customer's presets.
  useEffect(() => {
    if (enrichment.checkout) {
      dispatch({ type: 'SET_PAYMENT_CONTEXT', payload: enrichment.checkout });
    }
  }, [enrichment.checkout, dispatch]);
  useEffect(() => {
    if (enrichment.packages) {
      dispatch({ type: 'SET_INITIAL_PACKAGES', payload: enrichment.packages });
    }
  }, [enrichment.packages, dispatch]);
  useEffect(() => {
    if (enrichment.offers) {
      dispatch({ type: 'SET_AVAILABLE_ADDONS', payload: enrichment.offers.externalAddons });
    }
  }, [enrichment.offers, dispatch]);

  // Auto-select the preset time once slots finish loading.
  // Time slots are fetched asynchronously by useBookingEffects after
  // sport/type/duration/date are set, so we watch for timeSlots to
  // populate and match the preset time. Runs once then clears.
  const presetTimeApplied = useRef(false);

  useEffect(() => {
    if (
      !presets?.time ||
      presetTimeApplied.current ||
      state.isTimeSlotsLoading ||
      state.timeSlots.length === 0
    ) {
      return;
    }

    // Normalize to HH:MM for comparison (slots may be HH:MM or HH:MM:SS)
    const target = presets.time.slice(0, 5);
    const match = state.timeSlots.find(
      (s) => s.start_time.slice(0, 5) === target,
    );

    if (match) {
      presetTimeApplied.current = true;
      handleTimeSelect(match.start_time);
    }
  }, [
    presets?.time,
    state.timeSlots,
    state.isTimeSlotsLoading,
    handleTimeSelect,
  ]);

  // Auto-select the preset coach after time selection populates resources.
  const presetCoachApplied = useRef(false);

  useEffect(() => {
    if (
      !presets?.coachName ||
      presetCoachApplied.current ||
      !state.selectedTime
    ) {
      return;
    }

    const slot = state.timeSlots.find(
      (s) => s.start_time === state.selectedTime,
    );
    if (!slot?.available_resources) return;

    const coach = slot.available_resources.find(
      (r) => r.name === presets.coachName,
    );
    if (!coach) return;

    presetCoachApplied.current = true;

    const isPeak = slot.is_peak ?? false;
    // Phase B: prefer the server-resolved final_price on the coach resource.
    const price =
      coach.final_price ??
      (state.isMember && coach.member_price != null
        ? coach.member_price
        : isPeak
          ? coach.peak_price
          : coach.normal_price);

    dispatch({
      type: 'SELECT_TRAINER',
      payload: { trainerId: coach.id, price, isPeakTime: isPeak },
    });
  }, [
    presets?.coachName,
    state.selectedTime,
    state.timeSlots,
    state.isMember,
    dispatch,
  ]);

  // Effects (time slots, classes, credits)
  const { durationOptions, getDurationFromLabel, formatDuration } =
    useBookingEffects({
      state,
      dispatch,
      tenantSlug: initialData.tenantSlug,
      userId: user?.id,
      provider: initialData.provider,
      getAllBookingSlotsBatch,
      getAvailableClassesForDate,
      checkUserPackageCreditsAction,
    });

  // Derived values
  const availableBookingTypes = useMemo(() => {
    if (!state.sport) return [];
    return state.allBookingTypesBySport[state.sport] || [];
  }, [state.sport, state.allBookingTypesBySport]);

  // Templates for the active sport + booking type.
  const serviceTemplates = useMemo(() => {
    if (!state.sport || isClassType(state.bookingType)) return [];
    const all = state.allTemplatesBySport[state.sport] ?? [];
    return all.filter((tmpl) => {
      if (state.bookingType === 'court') return tmpl.has_court_booking;
      if (state.bookingType === 'trainer') return tmpl.has_trainer_booking;
      if (state.bookingType === 'ball_machine')
        return tmpl.has_ball_machine_booking;
      return false;
    });
  }, [state.sport, state.bookingType, state.allTemplatesBySport]);

  // Service-list flow: present a named service list when the duration chips
  // can't disambiguate the template — i.e. same-duration court templates that
  // share a physical court (the court picker can't tell them apart), or any
  // same-duration trainer/ball-machine collision (no court-level pick exists).
  // When same-duration court templates own disjoint courts, the downstream
  // court picker resolves template + price, so we stay on the duration chips.
  const showServiceList = useMemo(
    () =>
      shouldShowServiceList(
        state.bookingType,
        serviceTemplates,
        initialData.businessType,
      ),
    [state.bookingType, serviceTemplates, initialData.businessType],
  );

  // Dynamic-slot court bookings normally auto-pick the single best court and
  // hide the court picker. But when the picked duration is served by more than
  // one TEMPLATE (= distinct bookable product, e.g. Indoor vs Outdoor, or
  // Main vs Center vs Premium), the customer must see the picker — even if only
  // the best court of one product is currently available — so they know which
  // product/court they're booking. Gating on distinct templates (not price)
  // matters because two products can share a price (PD Jeddah Center == Premium
  // == 380): a price gate would let one silently auto-select; a template gate
  // surfaces both. Keyed on the duration's templates (NOT just the
  // currently-available courts) so the picker still shows when the other
  // product is fully booked.
  const durationHasMultipleProducts = useMemo(() => {
    if (state.bookingType !== 'court' || !state.selectedDuration) return false;

    const duration = getDurationFromLabel(state.selectedDuration);
    // getDurationFromLabel returns 0 when selectedDuration's label isn't in
    // durationOptions — a transient mismatch after an i18n language switch
    // (selectedDuration stores the localized LABEL, and durationOptions'
    // labels are re-formatted in the new language before SELECT_DURATION
    // re-fires with the new label). Fail OPEN: show the picker rather than
    // risk hiding it and silently auto-selecting the wrong product. Harmless —
    // the worst case is an extra (briefly empty) card that resolves the moment
    // the duration label settles.
    if (duration === 0) return true;

    const durationTemplateIds = new Set(
      serviceTemplates.filter((t) => t.duration === duration).map((t) => t.id),
    );
    return durationTemplateIds.size > 1;
  }, [
    state.bookingType,
    state.selectedDuration,
    serviceTemplates,
    getDurationFromLabel,
  ]);

  // Deep links lock the page in two layers (see coach-card.tsx and the
  // home-screen builder's "Book by sport" dropdown for the canonical builders):
  //
  //   Layer 1 — sport-level: /book?activity=<sport> (legacy ?sport=<sport>).
  //     Hard-locks the sport — the cross-sport switcher is hidden and the card
  //     header shows the sport's name. The in-sport BookingTypeToggle (court /
  //     coaching / classes …) and packages still render so the customer picks
  //     the service.
  //
  //   Layer 2 — service-level: /book?activity=<sport>&type=<service>. Adds a
  //     booking-type lock on top of layer 1 — the toggle AND the package strip
  //     are hidden, dropping the customer straight onto date → duration → time
  //     for that one service. This is what a coach deep link and a "Book Padel —
  //     Court Only" quick action produce.
  const lockedSportSlug = useMemo(() => {
    const slug = presets?.sport;
    if (!slug) return null;
    return state.availableSports.includes(slug) ? slug : null;
  }, [presets?.sport, state.availableSports]);

  const lockedSportName = lockedSportSlug
    ? (state.sportsMetadata[lockedSportSlug]?.name ?? null)
    : null;

  // The service lock only engages when the preset type is a real service of the
  // locked sport — a stale/foreign ?type= falls back to layer-1 (sport only)
  // rather than hiding the toggle with nothing valid selected.
  const lockedBookingType = useMemo(() => {
    if (!lockedSportSlug) return null;
    const type = presets?.bookingType;
    if (!type || type === ALL_CLASSES_BOOKING_TYPE) return null;
    const sportTypes = state.allBookingTypesBySport[lockedSportSlug] ?? [];
    return sportTypes.includes(type) ? type : null;
  }, [lockedSportSlug, presets?.bookingType, state.allBookingTypesBySport]);

  // Single-sport studio (e.g. Karama): the lone sport pill is redundant with
  // "All Classes" (selecting either returns the same set), and any extra
  // category like "Event" would otherwise be stranded in a separate box below.
  // Collapse to one row: "All Classes" + the sport's generic-class categories
  // as sibling pills. Multi-sport studios (FrameStudio) keep the sport row +
  // per-sport category box, since there category is a sub-filter of the chosen
  // sport, not a top-level axis.
  const singleSportStudio =
    unifiedClassMode && state.availableSports.length === 1;

  const studioCategoryPills = useMemo(() => {
    if (!singleSportStudio) return [];
    return availableBookingTypes
      .filter((type) => !isGenericClassType(type))
      .map((type) => {
        const { icon, label } = resolveBookingTypeChip(type, t);
        return {
          value: type,
          label,
          icon,
          isActive: state.bookingType === type,
          onSelect: () =>
            dispatch({ type: 'SELECT_BOOKING_TYPE', payload: type }),
        };
      });
  }, [
    singleSportStudio,
    availableBookingTypes,
    state.bookingType,
    dispatch,
    t,
  ]);

  const visibleLocations = useMemo(
    () =>
      state.locations.filter(
        (l) =>
          l.accessMode !== 'link_only' || l.id === state.selectedLocationId,
      ),
    [state.locations, state.selectedLocationId],
  );

  // Whether the left packages column should render. Hidden under a service-level
  // lock (?activity=&type=), AND when the tenant has no packages at all —
  // PackageSelection returns null in that case, so keeping the column mounted
  // would leave an empty 1/4-width gutter that pushes the booking form off-centre.
  // When hidden, the right column expands to full width (see below) and the
  // container's mx-auto keeps it centred.
  // On the native layout a tenant can additionally suppress the strip entirely
  // from the App Builder ('hidden'); the web layout ignores that setting.
  const packagesDisplay = initialData.packagesDisplay;
  const showPackagesColumn =
    !lockedBookingType &&
    state.packages.length > 0 &&
    !(isNativeLayout && packagesDisplay === 'hidden');

  // Schools tenants with many venues get the progressive drill-down picker
  // (Region → City → Gender → Club) instead of the flat card grid. Gated on
  // business type AND a meaningful number of locations so a small schools
  // tenant still gets the simpler cards. The funnel reads the rich
  // get_public_locations rows (region/city/gender/neighborhood metadata).
  const useSchoolsPicker =
    initialData.businessType === 'schools' &&
    initialData.publicLocations.length > 5;

  // Single-court hero: schools only. Other business types keep the standard
  // "Select Court" card even for one-court venues — a padel club with a single
  // court still reads as a court *choice*, whereas a school hall is just the
  // venue. Resolved here (not inside the component) so the same condition can
  // suppress the now-redundant picker below.
  const singleCourtHeroCourt = useMemo(() => {
    if (initialData.businessType !== 'schools') return null;
    if (state.bookingType !== 'court') return null;

    const sportId = state.sport ? state.sportsMetadata[state.sport]?.id : null;
    if (!sportId) return null;

    // Single-location tenants never render the picker, so selectedLocationId
    // legitimately stays null while the form is usable (see showBookingForm's
    // visibleLocations.length <= 1 shortcut). Fall back to the only location.
    const locationIds = Object.keys(initialData.courtsByLocation);
    const activeLocationId =
      state.selectedLocationId ??
      (locationIds.length === 1 ? locationIds[0]! : null);
    if (!activeLocationId) return null;

    const sportCourts = (
      initialData.courtsByLocation[activeLocationId] ?? []
    ).filter((court) => court.sport_id === sportId);

    if (sportCourts.length !== 1) return null;

    const court = sportCourts[0]!;
    return (court.image_urls?.length ?? 0) > 0 ? court : null;
  }, [
    initialData.businessType,
    initialData.courtsByLocation,
    state.bookingType,
    state.sport,
    state.sportsMetadata,
    state.selectedLocationId,
  ]);

  // The location picker renders when there's a real choice to make. The schools
  // funnel is driven by publicLocations (get_public_locations) — a school must
  // appear in the picker even before its courts are configured, so it canNOT be
  // gated on visibleLocations (which is derived from get_available_booking_types
  // and is empty until a location has bookable courts). The card selector keeps
  // its visibleLocations gate (normal tenants have courts wherever they book).
  const showLocationPicker =
    state.initialDataLoaded &&
    (useSchoolsPicker
      ? initialData.publicLocations.length >= 2
      : visibleLocations.length >= 2);

  // Locale-aware name/city per location id for the legacy LocationSelector
  // (its LocationInfo rows carry no Arabic). Resolved from the richer
  // get_public_locations rows so the card view shows Arabic in RTL, matching
  // the schools funnel. English locale leaves the map empty (cards fall back to
  // the LocationInfo primary).
  const localizedLocationLabels = useMemo(() => {
    if (!isRTL) return undefined;
    const map: Record<string, { name?: string; city?: string | null }> = {};
    for (const loc of initialData.publicLocations) {
      const name = loc.name_alt?.trim();
      const city = loc.city_alt?.trim();
      if (name || city)
        map[loc.id] = { name: name || undefined, city: city || null };
    }
    return map;
  }, [isRTL, initialData.publicLocations]);

  const showBookingForm =
    state.initialDataLoaded &&
    (visibleLocations.length <= 1 || !!state.selectedLocationId);

  const isClassBooking = isClassType(state.bookingType);
  const isTrainerBooking = state.bookingType === 'trainer';

  // Add-ons are scoped by category to the active booking type:
  //   court / ball_machine → 'court' add-ons (e.g. extra balls) + 'product'
  //   trainer (coach)      → 'resource' (coaching) add-ons + 'product'
  //   classes (group, etc) → 'product' only
  // 'product' add-ons (drinks/extras) always show, on every surface.
  const visibleAddons = useMemo(() => {
    const allowedCategories = new Set<string>(['product']);
    if (state.bookingType === 'court' || state.bookingType === 'ball_machine') {
      allowedCategories.add('court');
    } else if (state.bookingType === 'trainer') {
      allowedCategories.add('resource');
    }
    return state.availableAddons.filter(
      (addon) =>
        allowedCategories.has(addon.category) &&
        // Location-scoped add-ons only show at one of their own locations;
        // global (empty location_ids) add-ons show everywhere.
        (!addon.location_ids?.length ||
          !state.selectedLocationId ||
          addon.location_ids.includes(state.selectedLocationId)) &&
        // Service-scoped add-ons only show for their own templates; unscoped
        // (empty template_ids) add-ons show for every service. Until a
        // template is picked, scoped add-ons stay hidden (the selector only
        // renders once the booking is complete, so this rarely surfaces).
        (!addon.template_ids?.length ||
          (!!state.selectedTemplateId &&
            addon.template_ids.includes(state.selectedTemplateId))),
    );
  }, [
    state.availableAddons,
    state.bookingType,
    state.selectedLocationId,
    state.selectedTemplateId,
  ]);
  // Selected class entry — source of date/time/template for the discount-code
  // preview (classes pick a card, not a time slot, so state.selectedTime is
  // empty for them).
  const selectedClassEntry =
    isClassBooking && state.selectedClass
      ? state.availableClasses.find((c) => c.booking_id === state.selectedClass)
      : undefined;
  // Per-venue cancellation windows, already resolved server-side against the
  // tenant defaults (@kit/bookings/utils is server-only, so the precedence rule
  // never gets re-implemented here). Falls back to the tenant numbers for
  // tenants with no venues, or before a venue is picked.
  // A single public location does not need a picker, so the reducer can
  // legitimately have no selected id while its booking form is usable. Use
  // that sole public location for policy copy as well.
  const effectiveLocationId =
    state.selectedLocationId ??
    (visibleLocations.length === 1 ? visibleLocations[0]?.id : null);
  const venueCancellationWindows = effectiveLocationId
    ? initialData.locationCancellationWindows?.[effectiveLocationId]
    : undefined;
  const courtCancellationWindowHours =
    venueCancellationWindows?.court ?? initialData.cancellationWindowHours;
  const trainerCancellationWindowHours =
    venueCancellationWindows?.trainer ??
    initialData.cancellationWindowHoursTrainer ??
    initialData.cancellationWindowHours;
  const selectedClassPolicy = selectedClassEntry?.cancellationPolicy;
  const effectiveCancellationWindowHours = isClassBooking && selectedClassPolicy
    ? (selectedClassPolicy.cancellationWindowHours ?? courtCancellationWindowHours)
    : isTrainerBooking
    ? trainerCancellationWindowHours
    : courtCancellationWindowHours;
  const trainerSessionLabel =
    initialData.businessType === 'studio'
      ? t('policyTrainerSessionLabelStudio', {
          defaultValue: 'trainer sessions',
        })
      : t('policyTrainerSessionLabelCourt', {
          defaultValue: 'coach sessions',
        });
  // Tenant-authored cancellation policy for the active language (empty when the
  // tenant hasn't set one). When present it fully overrides the auto-generated
  // bullets below.
  const customCancellationText = pickPolicyText(
    initialData.bookingPolicy.cancellationPolicy,
    i18n.language,
  );
  // Tenant-authored terms text for the active language (empty when unset).
  const customTermsText = pickPolicyText(
    initialData.bookingPolicy.termsConditions,
    i18n.language,
  );
  const cancellationPolicyItems = useMemo(() => {
    if (isClassBooking && selectedClassEntry) {
      if (!selectedClassPolicy) return [t('classPolicyUnavailable')];
      if (selectedClassPolicy.isNonRefundable) {
        return [t('classPolicyNoRefunds')];
      }
      if (selectedClassPolicy.hasCustomWindow) {
        return [
          t(
            selectedClassPolicy.cancellationWindowHours === 0
              ? 'classPolicyCreditAnytime'
              : 'classPolicyCreditWindow',
            {
              window: t('policyHours', {
                count: selectedClassPolicy.cancellationWindowHours,
              }),
            },
          ),
        ];
      }
    }
    // Custom override: each non-empty line becomes a bullet, replacing the
    // generated policy entirely.
    if (customCancellationText) {
      return policyTextToBullets(customCancellationText);
    }

    // No inline defaultValue: passing one shadows the locale's plural keys
    // (policyHours_one / policyHours_other), which is why Arabic rendered the
    // literal English "24 hours". Let i18next resolve the plural from the
    // active namespace, with the EN keys as the natural fallback.
    const formatWindow = (hours: number) => t('policyHours', { count: hours });
    const items = [t('policyNonRefundable')];

    if (effectiveCancellationWindowHours === 0) {
      items.push(t('policyCreditAnytime'));
    } else {
      const windowLabel = formatWindow(effectiveCancellationWindowHours);
      items.push(
        t('policyCreditWindow', { window: windowLabel }),
        t('policyNoCreditWindow', { window: windowLabel }),
      );
    }

    // Mention the coach-session window only when it actually differs from the
    // one just described. Both sides are venue-resolved, so a venue that sets
    // only a general window no longer shows a spurious override line.
    if (
      trainerCancellationWindowHours !== courtCancellationWindowHours &&
      !isClassBooking &&
      !isTrainerBooking
    ) {
      if (trainerCancellationWindowHours === 0) {
        items.push(
          t('policyTrainerOverrideAnytime', {
            trainerSessionLabel,
          }),
        );
      } else {
        items.push(
          t('policyTrainerOverrideWindow', {
            trainerSessionLabel,
            window: formatWindow(trainerCancellationWindowHours),
          }),
        );
      }
    }

    items.push(t('policyNonTransferable'), t('policySplitNonRefundable'));

    return items;
  }, [
    isClassBooking,
    selectedClassEntry,
    selectedClassPolicy,
    customCancellationText,
    courtCancellationWindowHours,
    effectiveCancellationWindowHours,
    isTrainerBooking,
    t,
    trainerCancellationWindowHours,
    trainerSessionLabel,
  ]);

  // Per-template payment policy: allow_direct_payment=false forces
  // package-credit-only (card + Apple Pay hidden at checkout). This is a
  // template-level setting, so it applies to EVERY booking type that resolves
  // to a template — court, trainer, ball machine, and class — not only classes.
  // The tenant-wide class_package_card flag is a separate, class-only
  // kill-switch that stays scoped to class bookings until templates are
  // backfilled.
  const selectedTemplate =
    state.selectedTemplateId && state.sport
      ? (state.allTemplatesBySport[state.sport] ?? []).find(
          (t) => t.id === state.selectedTemplateId,
        )
      : undefined;
  const blockDirectPayment =
    selectedTemplate?.allow_direct_payment === false ||
    (isClassBooking && !state.classPackageCard);

  // Dates that have available classes (for greying out the date picker)
  const classAvailableDates = useMemo(() => {
    if (!isClassBooking) return undefined;
    // `undefined` means "dates unknown — don't disable anything". Reserve it for
    // the loading window only. Once the fetch resolves we must return a concrete
    // Set even when it's empty, so a category with zero upcoming sessions (e.g.
    // a single-sport studio's "Event" whose only session already passed) greys
    // out every day instead of leaving them all clickable-but-empty.
    if (state.isClassesLoading) return undefined;
    return new Set(state.allClassesForMonth.map((c) => c.booking_date));
  }, [isClassBooking, state.isClassesLoading, state.allClassesForMonth]);

  // Max advance booking limit: number of days from today the customer can select.
  // Falls back to 30 when the tenant has not configured a limit (NULL from business_settings).
  // Group classes are pre-generated by the tenant, so the limit doesn't apply — only the
  // explicit set of class dates gates availability.
  const maxAdvanceDays = state.maxAdvanceBookingDays ?? 30;
  const dateRangeDays = isClassBooking ? 365 : maxAdvanceDays;
  const bookingDateRange = getBookingDateRange(maxAdvanceDays);

  const disabledDates = (date: Date) => {
    if (date < bookingDateRange.startDate) return true;
    if (!isClassBooking && date > bookingDateRange.endDate) return true;
    if (!classAvailableDates) return false;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return !classAvailableDates.has(`${year}-${month}-${day}`);
  };

  const isBookingComplete =
    state.bookingPrice !== null &&
    ((isClassBooking && state.selectedClass) ||
      (state.selectedCourt &&
        (state.bookingType === 'court' ||
          (state.bookingType === 'trainer' && state.selectedTrainer) ||
          (state.bookingType === 'ball_machine' &&
            state.selectedBallMachine))));

  // Tenant opted into a gating acceptance checkbox above the pay button. When
  // on, no booking path (paid, free, cash, package) may proceed until the
  // customer ticks it.
  //
  // Waitlist mode is exempt: the checkbox is not rendered when the class is
  // full (same condition as the add-ons and payment-method blocks), so gating
  // "Join Waitlist" on it would leave a permanently disabled button with no
  // on-screen way to satisfy it. Joining a waitlist also takes no payment and
  // creates no cancellable booking — the policy is accepted later, at the
  // point the waitlist converts into a real enrollment.
  const requireAcceptance = initialData.bookingPolicy.requireAcceptance;
  const isAcceptanceBlocking =
    requireAcceptance && !hasAcceptedPolicy && !state.isSelectedClassFull;
  const hideWalletPayment = shouldHideClassWalletPayment({
    isClassBooking,
    templateAllowsDirectPayment: selectedTemplate?.allow_direct_payment,
  });
  const walletPaymentAvailability = getWalletPaymentAvailability({
    isGuest: !user,
    isHidden: hideWalletPayment,
    isEnabled: state.walletEnabled,
    bookingPrice: effectiveTotal,
    walletBalance: state.walletBalance,
  });
  const isSelectedWalletUnavailable =
    state.paymentMethod === 'wallet' && !walletPaymentAvailability.canPay;

  const isBookNowDisabled =
    !checkoutReady ||
    !state.date ||
    (isClassBooking && !state.selectedClass) ||
    (!isClassBooking && (!state.selectedTime || !state.selectedDuration)) ||
    (state.bookingType === 'court' && !state.selectedCourt) ||
    (state.bookingType === 'trainer' &&
      (!state.selectedTrainer || !state.selectedCourt)) ||
    (state.bookingType === 'ball_machine' &&
      (!state.selectedBallMachine || !state.selectedCourt)) ||
    isUserLoading ||
    state.isCreatingCheckout ||
    isSelectedWalletUnavailable ||
    isAcceptanceBlocking;

  // Location selection handler
  const handleLocationSelect = useCallback(
    async (locationId: string) => {
      const result = await getAvailableBookingTypes(
        initialData.tenantSlug,
        user?.id,
        locationId,
      );
      if (result.success && result.data) {
        dispatch({
          type: 'SET_LOCATION_DATA',
          payload: {
            locationId,
            bookingTypes: result.data.bookingTypes,
            sports: result.data.sports,
            durations: result.data.durations,
            durationsByBookingType: result.data.durationsByBookingType,
            templates: result.data.templates,
            packages: result.data.packages,
          },
        });

        // Remember this pick so the next visit skips the picker. Stored by
        // tenantId so multi-tenant users keep distinct preferences. Slug (not
        // UUID) is persisted because location slugs are stable but UUIDs are
        // tenant-internal and harder to debug if someone inspects storage.
        //
        // Two writes: cookie (primary, readable by the SSR loader so the next
        // page render has the right location on first paint with no flash) and
        // localStorage (fallback in case cookies are partitioned/blocked).
        if (typeof window !== 'undefined' && initialData.tenantId) {
          const matched = result.data.locations?.find(
            (l) => l.id === locationId,
          );
          if (matched?.slug) {
            const key = activeLocationCookieKey(initialData.tenantId);
            try {
              window.localStorage.setItem(key, matched.slug);
            } catch {
              // Quota / disabled storage — silent: persistence is a nice-to-have.
            }
            // 1 year expiry. SameSite=Lax keeps it first-party only. Secure
            // is gated on https so it works in local dev (http://localhost).
            const secure =
              window.location.protocol === 'https:' ? ';Secure' : '';
            document.cookie = `${key}=${encodeURIComponent(matched.slug)};path=/;max-age=31536000;SameSite=Lax${secure}`;

            // Notify any listeners in the same tab (e.g. the native top-bar
            // chip) that the active location changed. Storage events don't
            // fire same-tab, so we use a custom event.
            dispatchActiveLocationChanged({
              tenantId: initialData.tenantId,
              slug: matched.slug,
            });
          }
        }
      } else if (!result.success) {
        toast.error(t('toastLocationLoadFailed'));
      }
    },
    [initialData.tenantSlug, initialData.tenantId, user?.id, dispatch, t],
  );

  // Rehydrate last-picked location from localStorage. One-shot: only fires
  // once after INIT_DATA populates state.locations, only if the user hasn't
  // already landed on a deep-linked location and nothing has been selected
  // yet. The slug is validated against the current locations list so a stale
  // entry (location renamed / archived) silently falls through.
  const rehydratedLocationRef = useRef(false);
  useEffect(() => {
    if (rehydratedLocationRef.current) return;
    if (typeof window === 'undefined') return;
    if (!state.initialDataLoaded) return;
    if (state.selectedLocationId) return;
    if (state.locations.length <= 1) return;
    if (initialData.initialLocationId) return;
    if (!initialData.tenantId) return;

    let savedSlug: string | null = null;
    try {
      savedSlug = window.localStorage.getItem(
        activeLocationCookieKey(initialData.tenantId),
      );
    } catch {
      return;
    }
    if (!savedSlug) return;

    const match = state.locations.find((l) => l.slug === savedSlug);
    if (!match) return;

    rehydratedLocationRef.current = true;
    void handleLocationSelect(match.id);
  }, [
    state.initialDataLoaded,
    state.selectedLocationId,
    state.locations,
    initialData.initialLocationId,
    initialData.tenantId,
    handleLocationSelect,
  ]);

  const handleValidateLocationCode = useCallback(
    async (locationId: string, code: string): Promise<boolean> => {
      return validateLocationAccessCode(locationId, code);
    },
    [],
  );

  // Build checkout context from state
  const buildCheckoutContext = useCallback((): CheckoutContext => {
    const courtName =
      state.availableCourts.find((c) => c.id === state.selectedCourt)?.name ||
      'Unknown Court';
    const selectedClassData = state.availableClasses.find(
      (c) => c.booking_id === state.selectedClass,
    );

    // Phase B: derive the auto-applied membership perk for the selected slot.
    // The amount_off is the difference between original_price and final_price
    // on the slot (for court bookings) or on the selected resource (for
    // trainer/ball_machine bookings). We send { addonId, amountOff } to the
    // booking-creation action so it can bake in a court_booking_addons row.
    const selectedSlot = state.timeSlots.find(
      (s) => s.start_time === state.selectedTime,
    );
    let memberPerk: { addonId: string; amountOff: number } | null = null;
    if (selectedSlot?.member_discount) {
      let original: number | null = null;
      let final: number | null = null;
      if (state.bookingType === 'court') {
        // Prefer the SELECTED court's price tier — multi-tier slots have
        // different original/final per court, and the slot-level scalars
        // are only a single-tier fallback. Mirrors `perkAmountForCurrentSlot`
        // and `subtotalBeforePerk` so the recorded amount_off matches what
        // the user saw struck through.
        const selectedCourtForPerk = selectedSlot.available_courts?.find(
          (c) => c.id === state.selectedCourt,
        );
        original =
          selectedCourtForPerk?.original_price ??
          selectedSlot.original_price ??
          null;
        final =
          selectedCourtForPerk?.final_price ?? selectedSlot.final_price ?? null;
      } else {
        const selectedResourceId =
          state.bookingType === 'trainer'
            ? state.selectedTrainer
            : state.selectedBallMachine;
        const resource = selectedSlot.available_resources?.find(
          (r) => r.id === selectedResourceId,
        );
        if (resource) {
          original = resource.original_price ?? null;
          final = resource.final_price ?? null;
        }
      }
      if (original != null && final != null && original > final) {
        memberPerk = {
          addonId: selectedSlot.member_discount.addon_id,
          amountOff: Number((original - final).toFixed(2)),
        };
      }
    }

    return {
      bookingType: state.bookingType,
      date: state.date,
      selectedTime: state.selectedTime,
      selectedDuration: state.selectedDuration,
      selectedCourt: state.selectedCourt,
      selectedTrainer: state.selectedTrainer,
      selectedBallMachine: state.selectedBallMachine,
      selectedClass: state.selectedClass,
      isSelectedClassFull: state.isSelectedClassFull,
      // bookingPrice is the base court/resource price only — addon totals are
      // NOT included here. totalAmount passed to server actions must match the
      // server-side price validation (validateCourtBookingPrice), which only
      // knows about the court/resource price. Customer-selected addon prices are
      // validated and added server-side by insertBookingAddons after booking
      // creation; the DB trigger then updates booking_total automatically.
      bookingPrice: state.bookingPrice ?? 0,
      isPeakTime: state.isPeakTime,
      templateId: state.selectedTemplateId,
      numberOfParticipants: state.numberOfParticipants,
      membershipId: state.membershipId,
      selectedBookingAccount: state.selectedBookingAccount,
      paymentMethod: state.paymentMethod,
      splitPayerCount: state.splitPayerCount,
      user,
      guestUserId: state.guestUserId,
      guestEmail: state.guestEmail,
      guestName: state.guestName,
      guestPhone: state.guestPhone,
      courtName,
      className: selectedClassData?.class_name,
      tenantSlug: initialData.tenantSlug,
      tenantPublishableKey: state.tenantPublishableKey,
      tenantAccountId: state.tenantAccountId,
      getDurationFromLabel,
      calculateEndTime,
      selectedAddons: Array.from(state.selectedAddons.values()).map((a) => ({
        addonId: a.id,
        quantity: a.quantity,
      })),
      memberPerk,
      discountCode: appliedDiscount?.code ?? null,
    };
  }, [
    state,
    user,
    initialData.tenantSlug,
    getDurationFromLabel,
    appliedDiscount?.code,
  ]);

  // Build checkout callbacks
  const buildCheckoutCallbacks = useCallback((): CheckoutCallbacks => {
    return {
      onSuccess: (message: string) => {
        // Waitlist join — optimistically update state and show translated message
        if (message === 'waitlistJoined') {
          dispatch({ type: 'SET_ON_WAITLIST', payload: true });
          toast.success(t('waitlistJoined'));
          return;
        }
        toast.success(message);
      },
      onError: (message: string) =>
        toast.error(localizeBookingError(message, t)),
      onRedirect: (url: string) => {
        // The discount code (if any) is now redeemed by the booking-creation
        // action before this redirect runs, so the payment page will read the
        // post-discount booking_total directly — no URL piggybacking needed.
        router.push(url);
      },
      onHyperpayCheckout: (data) => {
        // The checkout module uses `unknown` for provider data to stay decoupled
        // from the reducer's internal types. Cast here at the boundary.
        dispatch({
          type: 'SET_CHECKOUT_DATA',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- provider data is opaque at this boundary
          payload: { kind: 'hyperpay', data: data as any },
        });
        dispatch({
          type: 'SET_DIALOG',
          payload: { dialog: 'isHyperpayDialogOpen', open: true },
        });
      },
      onMoyasarCheckout: (data) => {
        dispatch({
          type: 'SET_CHECKOUT_DATA',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- provider data is opaque at this boundary
          payload: { kind: 'moyasar', data: data as any },
        });
        dispatch({
          type: 'SET_DIALOG',
          payload: { dialog: 'isMoyasarDialogOpen', open: true },
        });
      },
      onOpenGuestDrawer: () => {
        dispatch({
          type: 'SET_DIALOG',
          payload: { dialog: 'isGuestDrawerOpen', open: true },
        });
      },
      onSetCreating: (creating: boolean) => {
        dispatch({ type: 'SET_CREATING_CHECKOUT', payload: creating });
      },
      onCreditsUpdated: (remaining: number) => {
        dispatch({
          type: 'SET_PACKAGE_CREDITS',
          payload: {
            availableCredits: remaining,
            hasMatchingDurationPackage: state.hasMatchingDurationPackage,
            // Preserve the current date-exclusion signal — this post-booking
            // refresh only changes the remaining count, not the selected date.
            hasDateExcludedCredits: state.hasDateExcludedCredits,
          },
        });
      },
      onApplePayClick: () => handleApplePayClick(),
    };
  }, [
    dispatch,
    router,
    state.hasMatchingDurationPackage,
    state.hasDateExcludedCredits,
  ]);

  // Apple Pay click handler
  const handleApplePayClick = useCallback(() => {
    // The native <apple-pay-button> can't be `disabled`, so we gate on click:
    // when the acceptance checkbox is required and unticked, no-op with a hint.
    if (isAcceptanceBlocking) {
      toast.error(t('toastAcceptPolicyRequired'));
      return;
    }

    if (
      !state.tenantPublishableKey ||
      !state.tenantAccountId ||
      state.bookingPrice === null
    ) {
      toast.error(t('toastPaymentConfigUnavailable'));
      return;
    }

    const courtName =
      state.availableCourts.find((c) => c.id === state.selectedCourt)?.name ||
      'Court';
    const selectedClassData = state.availableClasses.find(
      (c) => c.booking_id === state.selectedClass,
    );

    const existingGuestInfo =
      state.guestUserId && state.guestName && state.guestPhone
        ? {
            userId: state.guestUserId,
            name: state.guestName,
            phone: state.guestPhone,
            email: state.guestEmail || '',
          }
        : null;

    const addonsList = Array.from(state.selectedAddons.values()).map((a) => ({
      addonId: a.id,
      quantity: a.quantity,
    }));

    // Phase B: resolve the membership perk for this slot/resource so the
    // Apple Pay checkout action inserts the matching discount row.
    const selectedSlotForPerk = state.timeSlots.find(
      (s) => s.start_time === state.selectedTime,
    );
    let memberPerk: { addonId: string; amountOff: number } | null = null;
    if (selectedSlotForPerk?.member_discount) {
      let original: number | null = null;
      let final: number | null = null;
      if (state.bookingType === 'court') {
        // Prefer the SELECTED court's price tier — see buildCheckoutContext
        // above for the full rationale.
        const selectedCourtForPerk = selectedSlotForPerk.available_courts?.find(
          (c) => c.id === state.selectedCourt,
        );
        original =
          selectedCourtForPerk?.original_price ??
          selectedSlotForPerk.original_price ??
          null;
        final =
          selectedCourtForPerk?.final_price ??
          selectedSlotForPerk.final_price ??
          null;
      } else {
        const selectedResourceId =
          state.bookingType === 'trainer'
            ? state.selectedTrainer
            : state.selectedBallMachine;
        const resource = selectedSlotForPerk.available_resources?.find(
          (r) => r.id === selectedResourceId,
        );
        if (resource) {
          original = resource.original_price ?? null;
          final = resource.final_price ?? null;
        }
      }
      if (original != null && final != null && original > final) {
        memberPerk = {
          addonId: selectedSlotForPerk.member_discount.addon_id,
          amountOff: Number((original - final).toFixed(2)),
        };
      }
    }

    const booking =
      isClassType(state.bookingType) && state.selectedClass
        ? {
            type: 'class' as const,
            price: state.bookingPrice,
            selectedClass: state.selectedClass,
            className: selectedClassData?.class_name || 'Class',
            addons: addonsList.length > 0 ? addonsList : undefined,
            addonsTotalPrice: state.addonsTotalPrice || undefined,
            discountCode: appliedDiscount?.code ?? null,
            discountAmountOff: appliedDiscount?.amountOff ?? 0,
          }
        : {
            type: 'court' as const,
            price: state.bookingPrice,
            date: state.date,
            selectedTime: state.selectedTime,
            selectedDuration: state.selectedDuration,
            selectedCourt: state.selectedCourt,
            courtName,
            templateId: state.selectedTemplateId,
            bookingType: state.bookingType as
              | 'court'
              | 'trainer'
              | 'ball_machine',
            isPeakTime: state.isPeakTime,
            membershipId: state.membershipId,
            selectedTrainer: state.selectedTrainer || undefined,
            selectedBallMachine: state.selectedBallMachine || undefined,
            selectedBookingAccount: state.selectedBookingAccount,
            getDurationFromLabel,
            calculateEndTime,
            addons: addonsList.length > 0 ? addonsList : undefined,
            addonsTotalPrice: state.addonsTotalPrice || undefined,
            numberOfParticipants: state.numberOfParticipants,
            memberPerk,
            discountCode: appliedDiscount?.code ?? null,
            discountAmountOff: appliedDiscount?.amountOff ?? 0,
          };

    const params: ApplePaySessionParams = {
      publishableKey: state.tenantPublishableKey,
      tenantAccountId: state.tenantAccountId,
      tenantSlug: initialData.tenantSlug,
      booking,
      user,
      existingGuestInfo,
      onSuccess: (redirectUrl: string) => {
        toast.success(t('toastPaymentSuccessRedirecting'));
        router.push(redirectUrl);
      },
      onGuestCreated: (userId, name, phone, email) => {
        dispatch({
          type: 'SET_GUEST_INFO',
          payload: {
            guestUserId: userId,
            guestName: name,
            guestPhone: phone,
            guestEmail: email,
          },
        });
      },
      onError: (message: string) =>
        toast.error(localizeBookingError(message, t)),
      onCancel: (message: string) => toast.info(message),
      onInfo: (message: string) => toast.info(message),
    };

    initiateApplePaySession(params);
  }, [
    state,
    user,
    initialData.tenantSlug,
    getDurationFromLabel,
    router,
    dispatch,
    appliedDiscount?.amountOff,
    appliedDiscount?.code,
    isAcceptanceBlocking,
    t,
  ]);

  // Book now click handler
  const handleClassSelect = useCallback(
    async (
      bookingId: string,
      price: number,
      templateId: string | null,
      isFull?: boolean,
    ) => {
      dispatch({
        type: 'SELECT_CLASS',
        payload: {
          classId: bookingId,
          price,
          templateId: templateId ?? undefined,
          isFull,
        },
      });

      if (isFull && user) {
        const { isOnWaitlist } = await checkWaitlistStatusAction(bookingId);
        dispatch({ type: 'SET_ON_WAITLIST', payload: isOnWaitlist });
      } else if (isFull) {
        // Guest — not on waitlist, clear checking state
        dispatch({ type: 'SET_ON_WAITLIST', payload: false });
      }
    },
    [dispatch, user],
  );

  const handleMobileClassSelect = useCallback(
    async (
      bookingId: string,
      price: number,
      templateId: string | null,
      isFull?: boolean,
    ) => {
      await handleClassSelect(bookingId, price, templateId, isFull);

      setTimeout(() => {
        const el = extrasSectionRef.current;
        if (!el) return;
        scrollToBookingElement(el, 80);
      }, 200);
    },
    [handleClassSelect],
  );

  // Auto-select the preset class once classes finish loading.
  const presetClassApplied = useRef(false);

  useEffect(() => {
    if (
      !presets?.classTemplateId ||
      presetClassApplied.current ||
      state.isClassesLoading ||
      state.availableClasses.length === 0
    ) {
      return;
    }

    const match = state.availableClasses.find(
      (c) => c.template_id === presets.classTemplateId,
    );

    if (match) {
      presetClassApplied.current = true;
      handleMobileClassSelect(
        match.booking_id,
        match.price_per_person,
        match.template_id,
        match.spots_remaining <= 0 || match.virtually_full,
      );
    }
  }, [
    presets?.classTemplateId,
    state.availableClasses,
    state.isClassesLoading,
    handleMobileClassSelect,
  ]);

  const handleLeaveWaitlist = useCallback(async () => {
    if (!state.selectedClass) return;

    dispatch({ type: 'SET_CREATING_CHECKOUT', payload: true });

    const result = await leaveClassWaitlistAction({
      bookingId: state.selectedClass,
    });

    if (result.success) {
      dispatch({ type: 'SET_ON_WAITLIST', payload: false });
      toast.success(t('waitlistLeft'));
    } else {
      toast.error(result.error ?? t('waitlistLeaveFailed'));
    }

    dispatch({ type: 'SET_CREATING_CHECKOUT', payload: false });
  }, [state.selectedClass, dispatch, t]);

  const handleBookNowClick = useCallback(async () => {
    if (!checkoutReady) return;
    const ctx = buildCheckoutContext();
    const cb = buildCheckoutCallbacks();
    await processCheckout(ctx, cb);
  }, [checkoutReady, buildCheckoutContext, buildCheckoutCallbacks]);

  // Guest registration success
  const handleGuestRegistrationSuccess = useCallback(
    (userId: string, email: string, name: string, phone: string) => {
      dispatch({
        type: 'SET_GUEST_INFO',
        payload: {
          guestUserId: userId,
          guestEmail: email,
          guestName: name,
          guestPhone: phone,
        },
      });
      dispatch({
        type: 'SET_DIALOG',
        payload: { dialog: 'isGuestDrawerOpen', open: false },
      });
      toast.success(t('toastProceedingToCheckout'));

      // Build the checkout context immediately and inject the freshly registered
      // guest info directly. We cannot wait for React state to flush because:
      // 1. The reducer dispatch is asynchronous relative to the render cycle
      // 2. buildCheckoutContext() reads from the current (pre-dispatch) state
      // Directly overriding the context fields is the correct and intentional
      // approach here — it avoids a timing-dependent setTimeout hack.
      const ctx = buildCheckoutContext();
      ctx.guestUserId = userId;
      ctx.guestEmail = email;
      ctx.guestName = name;
      ctx.guestPhone = phone;

      const cb = buildCheckoutCallbacks();
      processCheckout(ctx, cb);
    },
    [buildCheckoutContext, buildCheckoutCallbacks, dispatch],
  );

  // Package selection
  const handlePackageSelect = useCallback(
    (packageData: SelectedPackage) => {
      // Fixed-slot packages have their own purchase flow (/book/packages)
      // with a day+time grid — the regular checkout dialog can't handle them.
      if (packageData.is_fixed_slot) {
        router.push(`/book/packages?package=${packageData.id}`);
        return;
      }
      dispatch({ type: 'SET_SELECTED_PACKAGE', payload: packageData });
      setIsPackageDialogOpen(true);
    },
    [dispatch, router],
  );

  // Package checkout
  // Package purchase (provider branching + wallet) lives in a shared hook so
  // /book and the native /packages catalog cannot drift. This surface routes
  // the provider callbacks into the booking reducer; /packages uses local
  // state. See use-package-purchase.ts.
  const {
    purchase: handlePackageCheckout,
    purchaseWithWallet: handlePackageWalletCheckout,
    isProcessing: isProcessingPackage,
  } = usePackagePurchase({
    onCloseCheckoutDialog: useCallback(() => setIsPackageDialogOpen(false), []),
    onHyperpay: useCallback(
      (data: HyperpayCheckoutData) => {
        dispatch({
          type: 'SET_CHECKOUT_DATA',
          payload: { kind: 'hyperpay', data },
        });
        dispatch({
          type: 'SET_DIALOG',
          payload: { dialog: 'isHyperpayDialogOpen', open: true },
        });
      },
      [dispatch],
    ),
    onStripe: useCallback(
      (clientSecret: string) => {
        dispatch({
          type: 'SET_CHECKOUT_DATA',
          payload: { kind: 'stripe', data: clientSecret },
        });
        dispatch({
          type: 'SET_DIALOG',
          payload: { dialog: 'isStripeDialogOpen', open: true },
        });
      },
      [dispatch],
    ),
    onMoyasar: useCallback(
      (data: MoyasarCheckoutData) => {
        dispatch({
          type: 'SET_CHECKOUT_DATA',
          payload: { kind: 'moyasar', data },
        });
        dispatch({
          type: 'SET_DIALOG',
          payload: { dialog: 'isMoyasarDialogOpen', open: true },
        });
      },
      [dispatch],
    ),
    onWalletSuccess: useCallback(
      (newBalance: number) => {
        dispatch({
          type: 'SET_WALLET',
          payload: { walletBalance: newBalance, walletEnabled: true },
        });
        // The purchase is not visible on /book — send the customer to their
        // packages. (/packages keeps them in place; see the hook's docs.)
        router.push('/packages');
      },
      [dispatch, router],
    ),
  });

  // Dialog handlers
  const handleViewBookings = useCallback(() => {
    dispatch({
      type: 'SET_DIALOG',
      payload: { dialog: 'isSuccessDialogOpen', open: false },
    });
    router.push('/dashboard/bookings');
  }, [dispatch, router]);

  const handleHyperpaySuccess = useCallback(() => {
    dispatch({
      type: 'SET_DIALOG',
      payload: { dialog: 'isHyperpayDialogOpen', open: false },
    });
    dispatch({
      type: 'SET_CHECKOUT_DATA',
      payload: { kind: 'hyperpay', data: null },
    });
  }, [dispatch]);

  const handleMoyasarSuccess = useCallback(() => {
    dispatch({
      type: 'SET_DIALOG',
      payload: { dialog: 'isMoyasarDialogOpen', open: false },
    });
    dispatch({
      type: 'SET_CHECKOUT_DATA',
      payload: { kind: 'moyasar', data: null },
    });
  }, [dispatch]);

  // Attach click handler to Apple Pay button
  useEffect(() => {
    const button = applePayButtonRef.current;
    if (button && state.paymentMethod === 'applepay') {
      button.addEventListener('click', handleApplePayClick);
      return () => {
        button.removeEventListener('click', handleApplePayClick);
      };
    }
  }, [state.paymentMethod, handleApplePayClick]);

  // Moyasar booking details (computed)
  const moyasarBookingDetails = useMemo(() => {
    const md = state.moyasarCheckoutData;
    if (!md) return { amount: 0, description: '' };

    // NOTE: `description` here is NOT display copy — MoyasarCheckoutDialog
    // forwards it into `paymentConfig.description`, so it is stored on the
    // payment and rendered in the admin Payments list. It must stay English
    // and un-translated: a `t()` value would record whatever language the
    // payer's browser happened to be in, making the ledger column
    // unfilterable and leaking Arabic into reconciliation exports.
    if (md.purchaseId) {
      return {
        amount: md.packageData?.price || state.selectedPackage?.price || 0,
        description: buildPaymentDescription({
          surface: 'package_purchase',
          name: md.packageData?.name || state.selectedPackage?.name,
        }),
      };
    }
    if (md.isClassPayment && md.participantId) {
      return {
        amount: state.bookingPrice || 0,
        description: buildPaymentDescription({
          surface: 'class_registration',
          name: state.availableClasses.find(
            (c) => c.booking_id === state.selectedClass,
          )?.class_name,
        }),
      };
    }
    return {
      amount: (state.bookingPrice || 0) + state.addonsTotalPrice,
      description: buildPaymentDescription({
        surface: 'court_booking',
        name: state.selectedCourt
          ? state.availableCourts.find((c) => c.id === state.selectedCourt)
              ?.name
          : undefined,
        // Local-date parts (not toISOString) so a late-evening booking isn't
        // shifted a day by UTC — mirrors formatDate in _lib/payment/checkout.ts.
        date: state.date
          ? `${state.date.getFullYear()}-${String(state.date.getMonth() + 1).padStart(2, '0')}-${String(state.date.getDate()).padStart(2, '0')}`
          : undefined,
        startTime: state.selectedTime || undefined,
      }),
    };
  }, [
    state.moyasarCheckoutData,
    state.bookingPrice,
    state.addonsTotalPrice,
    state.availableClasses,
    state.selectedClass,
    state.selectedCourt,
    state.availableCourts,
    state.date,
    state.selectedTime,
    state.selectedPackage,
    t,
    i18n.language,
  ]);

  const moyasarReturnUrl = useMemo(() => {
    const md = state.moyasarCheckoutData;
    if (!md) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (md.purchaseId) {
      return `${origin}/book/return?type=package&purchase_id=${md.purchaseId}&package_id=${md.packageData?.id || ''}`;
    }
    if (md.isClassPayment && md.participantId) {
      return `${origin}/book/return?type=class&participant_id=${md.participantId}`;
    }
    return undefined;
  }, [state.moyasarCheckoutData]);

  return (
    <>
      {deferredData.map((promise, index) => (
        <Suspense key={index} fallback={null}>
          <BookingDataStream promise={promise} onResolve={receiveData} />
        </Suspense>
      ))}
      <Script
        src="https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"
        strategy="lazyOnload"
      />

      <BookingPageSurface dir={isRTL ? 'rtl' : 'ltr'}>
        <div
          className={cn(
            'flex flex-col gap-4',
            !isNativeLayout && 'lg:flex-row lg:gap-6',
          )}
        >
          {/* Mobile-only: Location picker at top. The schools funnel is wrapped
              in a Card so it forms a bounded, overflow-clipped column (its inner
              club cards then lay out against the Card width, not the spilling
              flex row). The legacy LocationSelector stays un-wrapped — it renders
              its own cards / self-bordered pill, so an outer Card would double
              the borders. */}
          {showLocationPicker && (
            <div
              className={cn('w-full min-w-0', !isNativeLayout && 'lg:hidden')}
            >
              {useSchoolsPicker ? (
                <Card className="overflow-hidden">
                  <CardContent className="p-3">
                    <LocationFunnelPicker
                      locations={initialData.publicLocations}
                      onLocationSelect={handleLocationSelect}
                      selectedLocationId={state.selectedLocationId}
                      imagesByLocation={initialData.locationImages}
                      preferAlt={isRTL}
                    />
                  </CardContent>
                </Card>
              ) : (
                <LocationSelector
                  locations={visibleLocations}
                  onLocationSelect={handleLocationSelect}
                  selectedLocationId={state.selectedLocationId}
                  onValidateCode={handleValidateLocationCode}
                  collapseOnSelect
                  imagesByLocation={initialData.locationImages}
                  localizedLabels={localizedLocationLabels}
                />
              )}
            </div>
          )}

          {/* Left Column - Packages. Hidden under a service-level lock
              (?activity=&type=): that flow is a focused "book this one service"
              shortcut, so the package upsell strip — which lists all packages,
              not just this service's — would be noise. Also hidden when the
              tenant has no packages, so its empty column doesn't leave a gutter.
              On mobile this column renders first (flex-col), so hiding it also
              removes the packages-on-top block. */}
          {showPackagesColumn && (
            <div
              className={cn('w-full min-w-0', !isNativeLayout && 'lg:w-1/4')}
            >
              <BookingPackageSuggestions mode={packagesDisplay}>
                <PackageSelection
                  packages={state.packages}
                  onPackageSelect={handlePackageSelect}
                  selectedPackageId={state.selectedPackage?.id}
                  publicDiscountCodes={initialData.publicDiscountCodes}
                />
              </BookingPackageSuggestions>
            </div>
          )}

          {/* Right Column - Booking Form. Expands and centres itself (max-width
              + mx-auto) when the packages column is hidden — under a service
              lock or when the tenant has no packages — so it isn't stranded at
              2/3 width with empty space beside it. */}
          <div
            data-native-booking-form
            className={cn(
              'w-full min-w-0',
              isNativeLayout ? 'space-y-2' : 'space-y-4',
              isNativeLayout
                ? undefined
                : showPackagesColumn
                  ? 'lg:w-2/3'
                  : 'lg:mx-auto lg:w-full lg:max-w-3xl',
            )}
          >
            {/* Loading skeleton */}
            {!state.initialDataLoaded && (
              <div className="space-y-4">
                <Card>
                  <CardContent className="p-2 md:p-4">
                    <Skeleton className="mb-3 h-6 w-32" />
                    <div className="flex gap-2">
                      <Skeleton className="h-10 w-full rounded-lg" />
                      <Skeleton className="h-10 w-full rounded-lg" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-2 md:p-4">
                    <Skeleton className="mb-3 h-6 w-40" />
                    <div className="grid grid-cols-7 gap-1">
                      {Array.from({ length: 14 }).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full rounded-md" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-2 md:p-4">
                    <Skeleton className="mb-3 h-6 w-36" />
                    <div className="flex gap-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-20 rounded-lg" />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Location Selector (desktop only) */}
            {showLocationPicker && (
              <div className={cn('hidden', !isNativeLayout && 'lg:block')}>
                <Card className="overflow-hidden">
                  <CardContent className="p-2 md:p-4">
                    {useSchoolsPicker ? (
                      <LocationFunnelPicker
                        locations={initialData.publicLocations}
                        onLocationSelect={handleLocationSelect}
                        selectedLocationId={state.selectedLocationId}
                        imagesByLocation={initialData.locationImages}
                        preferAlt={isRTL}
                      />
                    ) : (
                      <LocationSelector
                        locations={visibleLocations}
                        onLocationSelect={handleLocationSelect}
                        selectedLocationId={state.selectedLocationId}
                        onValidateCode={handleValidateLocationCode}
                        collapseOnSelect
                        imagesByLocation={initialData.locationImages}
                        localizedLabels={localizedLocationLabels}
                      />
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Booking Type Container. A service-level lock (?activity=&type=)
                hides this whole card — both the sport switcher and the in-sport
                toggle are gone, so the bare "Padel" header would be a Card with
                no controls. The customer drops straight onto date/duration/time
                for the one locked service. */}
            {showBookingForm && !lockedBookingType && (
              <Card>
                <CardContent className="p-2 md:p-4">
                  <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                    {lockedSportName ?? t('bookingType')}
                  </h3>

                  {/* A sport-locked deep link hides the cross-sport switcher,
                      EXCEPT on single-sport studios where this row also carries
                      the "All Classes" + category pills (there's no redundant
                      sport pill to hide). */}
                  {!(lockedSportSlug && !singleSportStudio) &&
                    (state.availableSports.length > 1 || unifiedClassMode) && (
                      <div className={isNativeLayout ? 'mb-2' : 'mb-4'}>
                        <SportFilterToggle
                          value={state.sport}
                          onChange={(sport) => {
                            // In unified mode, picking a specific sport exits
                            // "All Classes". SELECT_SPORT already replaces the
                            // synthetic 'all' bookingType with the new sport's
                            // first real type (since 'all' isn't in
                            // allBookingTypesBySport), so no extra dispatch is
                            // needed.
                            dispatch({ type: 'SELECT_SPORT', payload: sport });
                          }}
                          // Single-sport studios hide the redundant sport pill
                          // and surface the sport's categories beside "All
                          // Classes" instead (see studioCategoryPills). All other
                          // tenants keep the real sport row.
                          availableSports={
                            singleSportStudio ? [] : state.availableSports
                          }
                          sportsMetadata={state.sportsMetadata}
                          allClassesPill={
                            unifiedClassMode
                              ? {
                                  label: t('allClasses'),
                                  isActive:
                                    state.bookingType ===
                                    ALL_CLASSES_BOOKING_TYPE,
                                  onSelect: () =>
                                    dispatch({
                                      type: 'SELECT_BOOKING_TYPE',
                                      payload: ALL_CLASSES_BOOKING_TYPE,
                                    }),
                                }
                              : undefined
                          }
                          categoryPills={studioCategoryPills}
                        />
                      </div>
                    )}

                  {/* The per-booking-type box is redundant for single-sport
                      studios — its categories now live beside "All Classes"
                      above. Other tenants still render it. */}
                  {!singleSportStudio &&
                    (availableBookingTypes.length > 0 ? (
                      <BookingTypeToggle
                        value={state.bookingType}
                        onChange={(type) =>
                          dispatch({
                            type: 'SELECT_BOOKING_TYPE',
                            payload: type,
                          })
                        }
                        availableTypes={availableBookingTypes}
                        unifiedClassMode={unifiedClassMode}
                      />
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        {t('loadingBookingTypes')}
                      </p>
                    ))}
                </CardContent>
              </Card>
            )}

            {/* Single-court hero (schools only — see singleCourtHeroCourt).
                Shows the one-hall venue's court up top; the redundant "Select
                Court" picker below is suppressed by the same condition, and
                the reducer auto-selects the sole court on SELECT_TIME. */}
            {showBookingForm && singleCourtHeroCourt && (
              <SingleCourtHero court={singleCourtHeroCourt} />
            )}

            {showBookingForm && (
              <>
                {/* Date Selection - Mobile (horizontal strip) */}
                <div className={cn(!isNativeLayout && 'md:hidden')}>
                  <DateStrip
                    selected={state.date}
                    onSelect={(date) =>
                      dispatch({ type: 'SELECT_DATE', payload: date })
                    }
                    daysToShow={dateRangeDays}
                    dates={isClassBooking ? undefined : bookingDateRange.dates}
                    language={i18n.language}
                    availableDates={classAvailableDates}
                  />
                </div>

                {/* Date Selection - Tablet */}
                <div
                  className={cn(
                    'hidden',
                    !isNativeLayout && 'md:block xl:hidden',
                  )}
                >
                  <Calendar
                    mode="single"
                    numberOfMonths={1}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    selected={state.date}
                    onSelect={(date) =>
                      date && dispatch({ type: 'SELECT_DATE', payload: date })
                    }
                    disabled={disabledDates}
                    className="mx-auto rounded-lg border"
                    buttonVariant="ghost"
                    formatters={calendarFormatters}
                    style={{
                      // @ts-expect-error - CSS custom property for dynamic cell sizing
                      '--cell-size': '50px',
                    }}
                  />
                </div>

                {/* Desktop: Date, Duration, and Time in Two-Column Layout */}
                <div
                  className={cn(
                    'hidden',
                    !isNativeLayout && 'xl:grid xl:grid-cols-2 xl:gap-4',
                  )}
                >
                  <div className="flex items-start justify-start">
                    <Calendar
                      mode="single"
                      numberOfMonths={1}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      selected={state.date}
                      onSelect={(date) =>
                        date && dispatch({ type: 'SELECT_DATE', payload: date })
                      }
                      disabled={disabledDates}
                      className="rounded-lg border"
                      buttonVariant="ghost"
                      formatters={calendarFormatters}
                      style={{
                        // @ts-expect-error - CSS custom property for dynamic cell sizing
                        '--cell-size': '50px',
                      }}
                    />
                  </div>

                  <div className="space-y-4">
                    {isClassBooking ? (
                      <Card>
                        <CardContent className="p-4">
                          <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                            {t('selectClass')}
                          </h3>
                          <ClassSelection
                            classes={state.availableClasses}
                            selectedClass={state.selectedClass}
                            onClassSelect={handleMobileClassSelect}
                            selectedDate={state.date || new Date()}
                            isLoading={state.isClassesLoading}
                          />
                        </CardContent>
                      </Card>
                    ) : (
                      <>
                        {/* Service / Duration Selection */}
                        <Card>
                          <CardContent className="p-4">
                            <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                              {showServiceList
                                ? t('chooseService')
                                : t('slotDuration')}
                            </h3>

                            {showServiceList ? (
                              <ServiceList
                                services={serviceTemplates}
                                selectedTemplateId={state.selectedTemplateId}
                                isMember={state.isMember}
                                formatDuration={formatDuration}
                                onSelect={handleServiceSelect}
                              />
                            ) : durationOptions.length > 0 ? (
                              <div className="grid grid-cols-4 gap-2">
                                {durationOptions.map((option) => (
                                  <Button
                                    key={option.duration}
                                    data-native-booking-choice
                                    aria-pressed={
                                      state.selectedDuration === option.label
                                    }
                                    variant={
                                      state.selectedDuration === option.label
                                        ? 'default'
                                        : 'outline'
                                    }
                                    className="w-full"
                                    disabled={!option.isAvailable}
                                    onClick={() =>
                                      option.isAvailable &&
                                      state.selectedDuration !== option.label &&
                                      dispatch({
                                        type: 'SELECT_DURATION',
                                        payload: option.label,
                                      })
                                    }
                                  >
                                    {option.label}
                                  </Button>
                                ))}
                              </div>
                            ) : (
                              <p className="text-muted-foreground text-sm">
                                {state.date
                                  ? t('noAvailableDurationSlots')
                                  : t('pleaseSelectDateFirst')}
                              </p>
                            )}
                          </CardContent>
                        </Card>

                        {/* Time Selection */}
                        {state.date && state.selectedDuration && (
                          <div
                            ref={desktopServiceTimeSectionRef}
                            data-test="desktop-service-time-section"
                          >
                            <Card>
                              <CardContent className="p-4">
                                <div className="mb-3 flex flex-wrap items-baseline gap-x-2">
                                  <h3 className="text-primary text-lg font-semibold lg:text-xl">
                                    {t('selectTime')}
                                  </h3>
                                </div>
                                <StaffAvailabilityFilter
                                  staff={availableStaff}
                                  selectedStaffId={state.trainerFilterId}
                                  isLoading={state.isTimeSlotsLoading}
                                  onSelectStaff={(staffId) =>
                                    dispatch({
                                      type: 'SELECT_TRAINER_FILTER',
                                      payload: staffId,
                                    })
                                  }
                                />
                                <TimeSlotsGrid
                                  timeSlots={visibleTimeSlots}
                                  selectedTime={state.selectedTime}
                                  isLoading={state.isTimeSlotsLoading}
                                  onSelectTime={(time) =>
                                    dispatch({
                                      type: 'SELECT_TIME',
                                      payload: time,
                                    })
                                  }
                                  language={i18n.language}
                                  gridCols="grid-cols-4"
                                  t={t}
                                />
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Mobile/Tablet: Class Selection */}
                {isClassBooking && (
                  <Card className={cn(!isNativeLayout && 'xl:hidden')}>
                    <CardContent
                      className="p-2"
                      style={isNativeLayout ? { paddingBottom: 0 } : undefined}
                    >
                      <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                        {t('selectClass')}
                      </h3>
                      <ClassSelection
                        classes={state.availableClasses}
                        selectedClass={state.selectedClass}
                        onClassSelect={handleMobileClassSelect}
                        selectedDate={state.date || new Date()}
                        isLoading={state.isClassesLoading}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Mobile/Tablet: Service Selection (service-list sports) */}
                {!isClassBooking && showServiceList && (
                  <Card className={cn(!isNativeLayout && 'xl:hidden')}>
                    <CardContent className="p-2">
                      <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                        {t('chooseService')}
                      </h3>
                      <ServiceList
                        services={serviceTemplates}
                        selectedTemplateId={state.selectedTemplateId}
                        isMember={state.isMember}
                        formatDuration={formatDuration}
                        onSelect={handleServiceSelect}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Mobile/Tablet: Duration Selection */}
                {!isClassBooking && !showServiceList && (
                  <Card className={cn(!isNativeLayout && 'xl:hidden')}>
                    <CardContent className="p-2">
                      {/* 1–2 durations: title + buttons on one row.
                          3+ durations: title row, then a grid-cols-3 below. */}
                      <div
                        className={
                          durationOptions.length > 2
                            ? 'mb-3'
                            : 'flex items-center justify-between gap-2'
                        }
                      >
                        <h3 className="text-primary text-lg font-semibold lg:text-xl">
                          {t('slotDuration')}
                        </h3>
                        {durationOptions.length > 0 &&
                          durationOptions.length <= 2 && (
                            <div className="flex gap-2">
                              {durationOptions.map((option) => (
                                <Button
                                  key={option.duration}
                                  data-native-booking-choice
                                  aria-pressed={
                                    state.selectedDuration === option.label
                                  }
                                  variant={
                                    state.selectedDuration === option.label
                                      ? 'default'
                                      : 'outline'
                                  }
                                  disabled={!option.isAvailable}
                                  onClick={() =>
                                    option.isAvailable &&
                                    state.selectedDuration !== option.label &&
                                    dispatch({
                                      type: 'SELECT_DURATION',
                                      payload: option.label,
                                    })
                                  }
                                  className="w-[85px]"
                                >
                                  {option.label}
                                </Button>
                              ))}
                            </div>
                          )}
                      </div>

                      {durationOptions.length > 2 && (
                        <div
                          className="grid grid-cols-3 gap-2"
                          data-native-duration-grid
                          data-option-count={durationOptions.length}
                        >
                          {durationOptions.map((option) => (
                            <Button
                              key={option.duration}
                              data-native-booking-choice
                              aria-pressed={
                                state.selectedDuration === option.label
                              }
                              variant={
                                state.selectedDuration === option.label
                                  ? 'default'
                                  : 'outline'
                              }
                              className="w-full"
                              disabled={!option.isAvailable}
                              onClick={() =>
                                option.isAvailable &&
                                state.selectedDuration !== option.label &&
                                dispatch({
                                  type: 'SELECT_DURATION',
                                  payload: option.label,
                                })
                              }
                            >
                              {option.label}
                            </Button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Mobile/Tablet: Time Selection */}
                {!isClassBooking && state.date && state.selectedDuration && (
                  <div
                    ref={mobileServiceTimeSectionRef}
                    className={cn(!isNativeLayout && 'xl:hidden')}
                    data-test="mobile-service-time-section"
                  >
                    <Card>
                      <CardContent className="p-2">
                        <div className="mb-3 flex flex-wrap items-baseline gap-x-2">
                          <h3 className="text-primary text-lg font-semibold lg:text-xl">
                            {t('selectTime')}
                          </h3>
                        </div>
                        <StaffAvailabilityFilter
                          staff={availableStaff}
                          selectedStaffId={state.trainerFilterId}
                          isLoading={state.isTimeSlotsLoading}
                          onSelectStaff={(staffId) =>
                            dispatch({
                              type: 'SELECT_TRAINER_FILTER',
                              payload: staffId,
                            })
                          }
                        />
                        <TimeSlotsGrid
                          timeSlots={visibleTimeSlots}
                          selectedTime={state.selectedTime}
                          isLoading={state.isTimeSlotsLoading}
                          onSelectTime={handleTimeSelect}
                          language={i18n.language}
                          gridCols="grid-cols-3"
                          buttonHeight="h-10"
                          t={t}
                        />
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Court/Trainer/Ball Machine Selection.
                    Dynamic-slot court bookings normally auto-pick the single
                    best court and hide this card. We surface the picker when the
                    dynamic RPC returns multiple courts for the same timeslot
                    (tied scores or different price tiers), OR when the picked
                    duration is served by more than one product/template
                    (durationHasMultipleProducts) — so a customer never books one
                    product thinking it was another, even when only the best
                    court of a single product is currently available. */}
                <div ref={nextSectionRef} />
                {!isClassBooking &&
                  state.date &&
                  state.selectedDuration &&
                  state.selectedTime &&
                  // The hero above already shows this venue's only court, and
                  // the reducer auto-selects it — a one-option radio here would
                  // just repeat the same photo twice on one page.
                  //
                  // Fail OPEN, mirroring durationHasMultipleProducts: only
                  // suppress once the slot actually resolves to <= 1 bookable
                  // court AND the duration maps to a single product. The hero's
                  // count comes from the static court roster, but this picker
                  // also disambiguates price tiers / products on the same
                  // physical court — hiding it in those cases would silently
                  // book one product as another.
                  !(
                    singleCourtHeroCourt &&
                    state.availableCourts.length <= 1 &&
                    !durationHasMultipleProducts
                  ) &&
                  !(
                    state.bookingType === 'court' &&
                    state.sport &&
                    state.dynamicSlotSports.has(state.sport) &&
                    state.availableCourts.length <= 1 &&
                    !durationHasMultipleProducts
                  ) && (
                    <Card>
                      <CardContent className="p-2 md:p-4">
                        {state.bookingType === 'court' ? (
                          <>
                            <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                              {t('selectCourt')}
                            </h3>
                            {state.availableCourts.length > 0 ? (
                              <RadioGroup
                                value={state.selectedCourt}
                                onValueChange={handleCourtSelect}
                                className="grid grid-cols-2 gap-4"
                              >
                                {state.availableCourts.map((court) => {
                                  const label = selectCourtLabel(
                                    court,
                                    i18n.language,
                                  );
                                  return (
                                    <CourtItem
                                      key={court.id}
                                      court={label.name}
                                      id={court.id}
                                      value={court.id}
                                      courtType={label.type ?? undefined}
                                      images={court.image_urls}
                                      disabled={!court.isAvailable}
                                    />
                                  );
                                })}
                              </RadioGroup>
                            ) : (
                              <p className="text-muted-foreground text-sm">
                                {t('noCourtsAvailable')}
                              </p>
                            )}
                          </>
                        ) : state.bookingType === 'trainer' ? (
                          <>
                            <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                              {t('selectTrainer')}
                            </h3>
                            <TrainerSelection
                              trainers={
                                state.timeSlots
                                  .find(
                                    (slot) =>
                                      slot.start_time === state.selectedTime,
                                  )
                                  ?.available_resources?.filter(
                                    (resource) =>
                                      !state.trainerFilterId ||
                                      resource.id === state.trainerFilterId,
                                  )
                                  .map((r) => ({
                                    id: r.id,
                                    name: r.name,
                                    bio: r.bio,
                                    picture_url: r.picture_url,
                                    normal_price: r.normal_price,
                                    peak_price: r.peak_price,
                                    member_price: r.member_price,
                                    // Phase B: per-resource server-resolved
                                    // pricing — must be forwarded so trainers
                                    // in the same slot honour their own
                                    // peak/non-peak windows independently.
                                    original_price: r.original_price,
                                    final_price: r.final_price,
                                    per_person_addon_id: r.per_person_addon_id,
                                    per_person_price: r.per_person_price,
                                    max_participants: r.max_participants,
                                  })) || []
                              }
                              isPeakTime={
                                state.timeSlots.find(
                                  (slot) =>
                                    slot.start_time === state.selectedTime,
                                )?.is_peak ?? false
                              }
                              isMember={state.isMember}
                              selectedTrainerId={state.selectedTrainer}
                              onTrainerSelect={(
                                trainerId,
                                trainerPrice,
                                isPeak,
                              ) => {
                                dispatch({
                                  type: 'SELECT_TRAINER',
                                  payload: {
                                    trainerId,
                                    price: trainerPrice,
                                    isPeakTime: isPeak,
                                  },
                                });
                              }}
                            />

                            {state.selectedTrainer &&
                              state.perPersonPrice != null &&
                              state.perPersonPrice > 0 &&
                              state.maxParticipants > 1 && (
                                <div className="mt-4">
                                  <ParticipantSelector
                                    value={state.numberOfParticipants}
                                    max={state.maxParticipants}
                                    onChange={(count) =>
                                      dispatch({
                                        type: 'SET_PARTICIPANTS',
                                        payload: count,
                                      })
                                    }
                                  />
                                </div>
                              )}
                          </>
                        ) : (
                          <>
                            <h3 className="text-primary mb-3 text-lg font-semibold lg:text-xl">
                              {t('selectBallMachine')}
                            </h3>
                            <BallMachineSelection
                              machines={
                                state.timeSlots
                                  .find(
                                    (slot) =>
                                      slot.start_time === state.selectedTime,
                                  )
                                  ?.available_resources?.map((r) => ({
                                    id: r.id,
                                    name: r.name,
                                    bio: r.bio,
                                    picture_url: r.picture_url,
                                    normal_price: r.normal_price,
                                    peak_price: r.peak_price,
                                    member_price: r.member_price,
                                    // Phase B: per-machine server-resolved pricing.
                                    original_price: r.original_price,
                                    final_price: r.final_price,
                                  })) || []
                              }
                              isPeakTime={
                                state.timeSlots.find(
                                  (slot) =>
                                    slot.start_time === state.selectedTime,
                                )?.is_peak ?? false
                              }
                              isMember={state.isMember}
                              selectedMachineId={state.selectedBallMachine}
                              onMachineSelect={(
                                machineId,
                                machinePrice,
                                isPeak,
                              ) => {
                                dispatch({
                                  type: 'SELECT_BALL_MACHINE',
                                  payload: {
                                    ballMachineId: machineId,
                                    price: machinePrice,
                                    isPeakTime: isPeak,
                                  },
                                });
                              }}
                            />
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                {/* Account Selector */}
                {user &&
                  state.bookingPrice !== null &&
                  ((isClassBooking && state.selectedClass) ||
                    (state.selectedCourt &&
                      (state.bookingType === 'court' ||
                        (state.bookingType === 'trainer' &&
                          state.selectedTrainer) ||
                        (state.bookingType === 'ball_machine' &&
                          state.selectedBallMachine)))) && (
                    <AccountSelector
                      userId={user.id}
                      userName={
                        user.user_metadata?.name || user.email?.split('@')[0]
                      }
                      selectedAccountId={
                        state.selectedBookingAccount?.id || user.id
                      }
                      onChange={(account) =>
                        dispatch({
                          type: 'SET_BOOKING_ACCOUNT',
                          payload: account,
                        })
                      }
                    />
                  )}

                {/* Customer Add-ons (Drinks & Extras) — hidden in waitlist mode */}
                {selectedClassEntry && (
                  <ClassCancellationDisclosure policy={selectedClassPolicy} />
                )}

                {isBookingComplete &&
                  !state.isSelectedClassFull &&
                  visibleAddons.length > 0 && (
                    <CustomerAddonSelector
                      addons={visibleAddons}
                      selectedAddons={state.selectedAddons}
                      onToggle={(addon) =>
                        dispatch({ type: 'TOGGLE_ADDON', payload: addon })
                      }
                      onQuantityChange={(addonId, quantity) =>
                        dispatch({
                          type: 'SET_ADDON_QUANTITY',
                          payload: { addonId, quantity },
                        })
                      }
                    />
                  )}

                {/* Waitlist Status */}
                {isClassBooking &&
                  state.isSelectedClassFull &&
                  state.selectedClass &&
                  !state.isCheckingWaitlist &&
                  (state.isOnWaitlist ? (
                    <Card>
                      <CardContent className="space-y-3 p-4">
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center dark:bg-amber-950/20">
                          <p className="font-semibold text-amber-800 dark:text-amber-200">
                            {t('waitlistJoined')}
                          </p>
                        </div>
                        <Button
                          onClick={handleLeaveWaitlist}
                          disabled={state.isCreatingCheckout}
                          variant="outline"
                          className="w-full"
                          size="lg"
                        >
                          {state.isCreatingCheckout
                            ? t('loading')
                            : t('leaveWaitlist')}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-muted-foreground text-center text-sm">
                          {t('classFullWaitlist')}
                        </p>
                      </CardContent>
                    </Card>
                  ))}

                <div ref={extrasSectionRef} />

                {/* Membership perk badge — auto-applied member discount preview.
                    Hidden when a code is applied (no stacking). */}
                {isBookingComplete &&
                  !state.isSelectedClassFull &&
                  !state.isCheckingWaitlist &&
                  !isClassBooking &&
                  perkAmountForCurrentSlot && (
                    <div className="px-4 pt-2">
                      <div
                        className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20"
                        data-test="membership-perk-preview"
                      >
                        <p className="flex items-center gap-1 text-sm font-medium text-amber-900 dark:text-amber-200">
                          <span>
                            {perkAmountForCurrentSlot.planName
                              ? t('memberDiscountAppliedNamed', {
                                  planName: perkAmountForCurrentSlot.planName,
                                })
                              : t('memberDiscountApplied')}
                          </span>
                          <CurrencyIcon className="h-3.5 w-3.5" />
                          <span>
                            {perkAmountForCurrentSlot.amount.toFixed(2)}
                          </span>
                        </p>
                        <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                          {t('memberDiscountAutoDeducted')}
                        </p>
                      </div>
                    </div>
                  )}

                {/* Discount Code (preview only — applied/redeemed server-side after
                    the booking/enrollment is created).
                    Hidden when a member perk is already applied — only one
                    discount per booking, and the perk is auto-applied.
                    `perkAmountForCurrentSlot` reads original/final off the
                    selected slot, same source of truth as the strikethrough,
                    so the two states can't drift apart (court bookings only —
                    classes have no perk path).
                    Classes derive date/time/template from the selected class
                    entry instead of the slot picker, and include the selected
                    add-ons in draftTotal to match the participant redemption
                    base (amount_paid + addons_total). */}
                {isBookingComplete &&
                  !state.isSelectedClassFull &&
                  !state.isCheckingWaitlist &&
                  state.tenantAccountId &&
                  state.date &&
                  (isClassBooking
                    ? !!selectedClassEntry
                    : !!state.selectedTime) &&
                  (state.bookingPrice ?? 0) > 0 &&
                  !perkAmountForCurrentSlot && (
                    <div className={isNativeLayout ? 'py-1' : 'px-4 pt-2'}>
                      <DiscountCodeInput
                        mode={
                          isClassBooking && selectedClassEntry
                            ? {
                                kind: 'preview',
                                accountId: state.tenantAccountId,
                                bookingDate: formatDateForDiscount(state.date),
                                startTime: selectedClassEntry.start_time,
                                endTime: selectedClassEntry.end_time,
                                // Class redemption base is seat price + add-ons
                                // (admin participant-flow parity), so preview
                                // against the same total.
                                draftTotal:
                                  (state.bookingPrice ?? 0) +
                                  (state.addonsTotalPrice ?? 0),
                                templateId:
                                  selectedClassEntry.template_id ?? undefined,
                                // Class slug ('group_classes', 'kids_academy',
                                // …) — same vocabulary kit.booking_resource_kind
                                // resolves server-side at redeem time.
                                bookingType: state.bookingType ?? undefined,
                                // Location-scoped codes only preview as valid
                                // at their own location (redeem re-checks).
                                locationId:
                                  state.selectedLocationId ?? undefined,
                              }
                            : {
                                kind: 'preview',
                                accountId: state.tenantAccountId,
                                bookingDate: formatDateForDiscount(state.date),
                                startTime: state.selectedTime,
                                // Pass end time so the server can enforce that the
                                // *entire* booking falls inside the discount's hour
                                // window (not just the start). Falls back to
                                // start-only check when the duration isn't selected.
                                endTime: state.selectedDuration
                                  ? calculateEndTime(
                                      state.selectedTime,
                                      getDurationFromLabel(
                                        state.selectedDuration,
                                      ),
                                    )
                                  : undefined,
                                // Discount applies to the slot/resource price only —
                                // never to customer-selected addons. Mirrors the
                                // server-side cap in redeem_discount_code.
                                draftTotal: state.bookingPrice ?? 0,
                                templateId:
                                  state.selectedTemplateId ?? undefined,
                                bookingType: state.bookingType ?? undefined,
                                // Location-scoped codes only preview as valid
                                // at their own location (redeem re-checks).
                                locationId:
                                  state.selectedLocationId ?? undefined,
                              }
                        }
                        applied={appliedDiscount}
                        onApplied={(d) => setAppliedDiscount(d)}
                        onRemoved={() => setAppliedDiscount(null)}
                        // Tenant-advertised codes for this surface — one-tap
                        // buttons; a lone code is auto-applied. Display-level
                        // narrowing only; the preview RPC re-validates (day/
                        // hour windows, usage caps) on selection.
                        suggestedCodes={publicCodesForSlotSurface(
                          initialData.publicDiscountCodes,
                          {
                            bookingDate: formatDateForDiscount(state.date),
                            bookingType: state.bookingType,
                            templateId: isClassBooking
                              ? selectedClassEntry?.template_id
                              : state.selectedTemplateId,
                            locationId: state.selectedLocationId,
                          },
                        ).map((c) => ({
                          code: c.code,
                          name: c.name,
                          discountType: c.discount_type,
                          discountValue: Number(c.discount_value),
                        }))}
                        autoApplySingle
                        // Must mirror the reset effect that clears
                        // appliedDiscount: date/time/duration/price are already
                        // in the preview mode, so only court, class and the
                        // add-ons total need to be threaded through here.
                        autoApplyContextKey={[
                          state.selectedCourt,
                          state.selectedClass,
                          state.addonsTotalPrice ?? 0,
                        ].join(':')}
                      />
                    </div>
                  )}

                <div ref={paymentSectionRef} />

                {/* Free booking summary — replaces payment-method picker when
                    the effective total is 0 (e.g. 100% discount coupon). */}
                {isBookingComplete &&
                  !state.isSelectedClassFull &&
                  !state.isCheckingWaitlist &&
                  isFreeBooking && (
                    <div className="rounded-lg border bg-green-50 p-4 text-center">
                      <p className="text-sm font-semibold text-green-700">
                        {t('freeBookingTitle', {
                          defaultValue: 'No payment required',
                        })}
                      </p>
                      <p className="mt-1 text-xs text-green-700/80">
                        {t('freeBookingSubtitle', {
                          defaultValue:
                            'Your booking is on the house — confirm to lock in the slot.',
                        })}
                      </p>
                    </div>
                  )}

                {/* Payment Method Selector — hidden when class is full (waitlist mode) or free */}
                {isBookingComplete &&
                  !state.isSelectedClassFull &&
                  !state.isCheckingWaitlist &&
                  !isFreeBooking && (
                    <PaymentMethodSelector
                      availableCredits={user ? state.availableCredits : 0}
                      hasMatchingDuration={
                        user ? state.hasMatchingDurationPackage : false
                      }
                      walletBalance={user ? state.walletBalance : 0}
                      walletEnabled={user ? state.walletEnabled : false}
                      hideWalletPayment={hideWalletPayment}
                      creditsHiddenForDate={
                        user ? state.hasDateExcludedCredits : false
                      }
                      showPackageParticipantHint={
                        !isClassBooking && state.numberOfParticipants > 1
                      }
                      bookingPrice={effectiveTotal}
                      originalPrice={strikeReference ?? undefined}
                      selectedMethod={state.paymentMethod}
                      onMethodChange={(method: PaymentMethod) =>
                        dispatch({
                          type: 'SET_PAYMENT_METHOD',
                          payload: method,
                        })
                      }
                      disabled={
                        !state.date ||
                        (!isClassBooking &&
                          (!state.selectedTime ||
                            !state.selectedDuration ||
                            !state.selectedCourt)) ||
                        (isClassBooking && !state.selectedClass)
                      }
                      isGuest={!user}
                      isApplePayAvailable={
                        state.isApplePayAvailable &&
                        initialData.provider === 'moyasar'
                      }
                      splitPayerCount={state.splitPayerCount}
                      onSplitPayerCountChange={(count) =>
                        dispatch({
                          type: 'SET_SPLIT_PAYER_COUNT',
                          payload: count,
                        })
                      }
                      splitComp={splitComp}
                      maxSplitPayers={
                        state.bookingType === 'trainer' &&
                        state.numberOfParticipants > 1
                          ? state.numberOfParticipants
                          : undefined
                      }
                      autoSelectSplitCount={
                        state.bookingType === 'trainer' &&
                        state.numberOfParticipants > 1
                          ? state.numberOfParticipants
                          : undefined
                      }
                      hideSplitPayment={
                        !state.enableSplitPayment ||
                        isClassBooking ||
                        (state.bookingType === 'trainer' &&
                          state.perPersonPrice != null &&
                          state.numberOfParticipants <= 1)
                      }
                      hideCashPayment={!state.enableCashPayment}
                      hideCardPayment={blockDirectPayment}
                    />
                  )}

                <BookingPolicyNotice
                  visible={!!isBookingComplete && !state.isSelectedClassFull}
                  requireAcceptance={requireAcceptance}
                  accepted={hasAcceptedPolicy}
                  onAcceptedChange={setHasAcceptedPolicy}
                  cancellationPolicyItems={cancellationPolicyItems}
                  termsText={customTermsText}
                >
                  {/* Book Now / Apple Pay / Join Waitlist Button */}
                  {isBookingComplete &&
                    !state.isCheckingWaitlist &&
                    !state.isOnWaitlist &&
                    (state.paymentMethod === 'applepay' &&
                    !state.isSelectedClassFull &&
                    !blockDirectPayment &&
                    !isFreeBooking ? (
                      // dir="ltr": the native <apple-pay-button> otherwise
                      // inherits the page's dir="rtl" (Arabic) and Apple's
                      // shadow-DOM lays the glyph + text right-to-left.
                      <div
                        dir="ltr"
                        className={cn(
                          'w-full',
                          // Native <apple-pay-button> has no `disabled`; when the
                          // acceptance checkbox is required-but-unticked we dim it
                          // and block pointer events (handleApplePayClick also
                          // no-ops defensively).
                          isAcceptanceBlocking &&
                            'pointer-events-none opacity-50',
                        )}
                      >
                        <style
                          dangerouslySetInnerHTML={{
                            __html: `
                          apple-pay-button {
                            --apple-pay-button-width: 100%;
                            --apple-pay-button-height: 40px;
                            --apple-pay-button-border-radius: var(--native-booking-control-radius, 6px);
                            --apple-pay-button-padding: 0px 0px;
                            --apple-pay-button-box-sizing: border-box;
                          }
                        `,
                          }}
                        />
                        <apple-pay-button
                          ref={(el: HTMLElement | null) => {
                            applePayButtonRef.current = el;
                          }}
                          buttonstyle="black"
                          type="book"
                          locale="en"
                        />
                      </div>
                    ) : (
                      <Button
                        className="w-full"
                        size="lg"
                        disabled={isBookNowDisabled}
                        onClick={handleBookNowClick}
                      >
                        {isUserLoading || state.isCreatingCheckout ? (
                          t('loading')
                        ) : (
                          <>
                            {isFreeBooking
                              ? isClassBooking
                                ? t('confirmFreeEnrollment', {
                                    defaultValue: 'Confirm enrollment',
                                  })
                                : t('confirmFreeBooking', {
                                    defaultValue: 'Confirm booking',
                                  })
                              : isClassBooking
                                ? state.isSelectedClassFull
                                  ? t('joinWaitlist')
                                  : state.paymentMethod === 'package'
                                    ? t('enrollWithCredit')
                                    : state.paymentMethod === 'cash'
                                      ? t('enrollPayAtVenue')
                                      : t('enrollAndPay')
                                : state.paymentMethod === 'package'
                                  ? t('bookWithCredit')
                                  : state.paymentMethod === 'cash'
                                    ? t('bookNowPayAtVenue')
                                    : state.paymentMethod === 'split'
                                      ? isSplitComp
                                        ? t('confirmSplitMemberComp', {
                                            defaultValue: 'Confirm',
                                          })
                                        : t('splitPaymentButton', {
                                            count: state.splitPayerCount,
                                          })
                                      : t('bookNowAndPay')}
                            {!isFreeBooking &&
                              !isSplitComp &&
                              state.bookingPrice !== null &&
                              state.paymentMethod !== 'package' &&
                              !state.isSelectedClassFull && (
                                <span
                                  dir="ltr"
                                  className="ms-2 inline-flex items-center gap-2 font-semibold"
                                >
                                  {strikeReference != null &&
                                    state.paymentMethod !== 'split' && (
                                      <span className="text-sm font-normal line-through opacity-70">
                                        {strikeReference.toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </span>
                                    )}
                                  <span className="inline-flex items-center gap-1">
                                    <CurrencyIcon className="h-5 w-5" />
                                    {state.paymentMethod === 'split'
                                      ? (
                                          effectiveTotal / state.splitPayerCount
                                        ).toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }) +
                                        ' ' +
                                        t('each')
                                      : effectiveTotal.toLocaleString('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                  </span>
                                </span>
                              )}
                          </>
                        )}
                      </Button>
                    ))}
                </BookingPolicyNotice>
              </>
            )}
          </div>
        </div>

        {/* Dialogs */}
        <GuestRegistrationDrawer
          isOpen={state.isGuestDrawerOpen}
          onClose={() =>
            dispatch({
              type: 'SET_DIALOG',
              payload: { dialog: 'isGuestDrawerOpen', open: false },
            })
          }
          onSuccess={handleGuestRegistrationSuccess}
          bookingState={{
            bookingType: state.bookingType,
            sport: state.sport,
            selectedDuration: state.selectedDuration,
            date: state.date,
            selectedTime: state.selectedTime,
            trainerName: state.selectedTrainer
              ? state.timeSlots
                  .find((s) => s.start_time === state.selectedTime)
                  ?.available_resources?.find(
                    (r) => r.id === state.selectedTrainer,
                  )?.name
              : undefined,
            selectedTemplateId: state.selectedTemplateId,
            selectedLocationId: state.selectedLocationId,
            locations: state.locations,
          }}
        />

        <CheckoutSuccessDialog
          isOpen={state.isSuccessDialogOpen}
          onClose={() =>
            dispatch({
              type: 'SET_DIALOG',
              payload: { dialog: 'isSuccessDialogOpen', open: false },
            })
          }
          onViewBookings={handleViewBookings}
        />

        {state.isHyperpayDialogOpen && (
        <HyperpayCheckoutDialog
          isOpen={state.isHyperpayDialogOpen}
          onClose={() =>
            dispatch({
              type: 'SET_DIALOG',
              payload: { dialog: 'isHyperpayDialogOpen', open: false },
            })
          }
          checkoutData={state.hyperpayCheckoutData}
          returnPath={state.hyperpayCheckoutData?.returnPath}
          onSuccess={handleHyperpaySuccess}
        />
        )}

        {state.isMoyasarDialogOpen && (
        <MoyasarCheckoutDialog
          isOpen={state.isMoyasarDialogOpen}
          onClose={() =>
            dispatch({
              type: 'SET_DIALOG',
              payload: { dialog: 'isMoyasarDialogOpen', open: false },
            })
          }
          checkoutData={state.moyasarCheckoutData}
          bookingDetails={moyasarBookingDetails}
          returnUrl={moyasarReturnUrl}
          onSuccess={handleMoyasarSuccess}
        />
        )}

        {state.isStripeDialogOpen && (
        <StripeCheckoutForm
          isOpen={state.isStripeDialogOpen}
          onClose={() =>
            dispatch({
              type: 'SET_DIALOG',
              payload: { dialog: 'isStripeDialogOpen', open: false },
            })
          }
          clientSecret={state.stripeClientSecret}
          publishableKey={state.tenantPublishableKey ?? undefined}
        />
        )}

        {isPackageDialogOpen && !checkoutReady && <CheckoutLoading />}
        {isPackageDialogOpen && checkoutReady && (
        <PackageCheckoutDialog
          isOpen={isPackageDialogOpen}
          onClose={() => {
            // If this dialog was opened by the first-time offer auto-open,
            // remember the dismissal for the rest of the session so it doesn't
            // reappear on every navigation back to /book.
            if (
              autoOpenedFirstTimeRef.current &&
              state.selectedPackage?.id === initialData.firstTimePackageId &&
              typeof window !== 'undefined' &&
              initialData.firstTimePackageId
            ) {
              window.sessionStorage.setItem(
                `firstTimeOffer:dismissed:${initialData.tenantId}:${initialData.firstTimePackageId}`,
                '1',
              );
            }
            setIsPackageDialogOpen(false);
            // The first-time offer step is over once its dialog closes; let the
            // profile-completion popup take its turn.
            setFirstTimeOfferResolved(true);
          }}
          packageData={state.selectedPackage}
          onCheckout={handlePackageCheckout}
          onWalletCheckout={handlePackageWalletCheckout}
          isProcessing={isProcessingPackage}
          isAuthenticated={!!user}
          userId={user?.id}
          userName={user?.user_metadata?.name || user?.email?.split('@')[0]}
          userPhone={
            // Account holder's phone (E.164) to prefill Player 1. Prefer the
            // metadata phone (already +-prefixed); fall back to auth phone
            // (stored without +).
            (user?.user_metadata?.phone_number as string | undefined) ||
            (user?.phone ? `+${user.phone.replace(/^\+/, '')}` : undefined)
          }
          tenantId={state.tenantAccountId || undefined}
          tenantName={initialData.tenantSlug}
          publishableKey={state.tenantPublishableKey}
          provider={initialData.provider}
          locationId={state.selectedPackage?.location_id ?? undefined}
          publicDiscountCodes={initialData.publicDiscountCodes}
          walletEnabled={user ? state.walletEnabled : false}
          walletBalance={user ? state.walletBalance : 0}
        />
        )}

        <ProfileCompletionDialog
          open={isProfilePromptOpen}
          onOpenChange={setIsProfilePromptOpen}
          missingFieldLabels={initialData.profileCompletion.missingFieldLabels}
        />
      </BookingPageSurface>
    </>
  );
}

// Extracted sub-component for time slot grids (used in both desktop and mobile)
function TimeSlotsGrid({
  timeSlots,
  selectedTime,
  isLoading,
  onSelectTime,
  language,
  gridCols,
  buttonHeight,
  t,
}: {
  timeSlots: { start_time: string; is_available: boolean }[];
  selectedTime: string;
  isLoading: boolean;
  onSelectTime: (time: string) => void;
  language: string;
  gridCols: string;
  buttonHeight?: string;
  t: (key: string) => string;
}) {
  const isNativeLayout = useNativeLayout();
  const slotStyle = isNativeLayout
    ? { borderRadius: 'var(--native-booking-control-radius, 12px)' }
    : undefined;

  if (isLoading) {
    return (
      <div className={`grid ${gridCols} gap-2`}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Skeleton
            key={i}
            style={slotStyle}
            className={`${buttonHeight || 'h-10'} w-full`}
          />
        ))}
      </div>
    );
  }

  if (timeSlots.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t('noTimeSlotsAvailable')}
      </p>
    );
  }

  const regularSlots = timeSlots.filter(
    (slot) => !isPostMidnight(slot.start_time),
  );
  const postMidnightSlots = timeSlots.filter((slot) =>
    isPostMidnight(slot.start_time),
  );

  return (
    <>
      {regularSlots.length > 0 && (
        <div className={`grid ${gridCols} gap-2`}>
          {regularSlots.map((slot) => (
            <Button
              key={slot.start_time}
              style={slotStyle}
              aria-pressed={selectedTime === slot.start_time}
              data-native-booking-choice
              variant={selectedTime === slot.start_time ? 'default' : 'outline'}
              className={`w-full ${buttonHeight || ''} px-1 text-xs sm:text-sm ${!slot.is_available ? 'bg-muted text-muted-foreground hover:bg-muted opacity-50' : ''}`}
              disabled={!slot.is_available}
              onClick={() => slot.is_available && onSelectTime(slot.start_time)}
            >
              {formatTimeToAmPm(slot.start_time, language)}
            </Button>
          ))}
        </div>
      )}

      {postMidnightSlots.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex items-center gap-2">
            <div className="bg-border h-px flex-1" />
            <span className="text-muted-foreground px-2 text-sm font-medium">
              {t('postMidnight')}
            </span>
            <div className="bg-border h-px flex-1" />
          </div>
          <div className={`grid ${gridCols} gap-2`}>
            {postMidnightSlots.map((slot) => (
              <Button
                key={slot.start_time}
                style={slotStyle}
                aria-pressed={selectedTime === slot.start_time}
                data-native-booking-choice
                variant={
                  selectedTime === slot.start_time ? 'default' : 'outline'
                }
                className={`w-full ${buttonHeight || ''} px-1 text-xs sm:text-sm ${!slot.is_available ? 'bg-muted text-muted-foreground hover:bg-muted opacity-50' : ''}`}
                disabled={!slot.is_available}
                onClick={() =>
                  slot.is_available && onSelectTime(slot.start_time)
                }
              >
                {formatTimeToAmPm(slot.start_time, language)}
              </Button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
